import { Component, OnInit, signal, ViewChild, ElementRef, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { SalaService } from '../../services/sala.service';
import { friendlyError } from '../../utils/friendly-error';
import { SpaceScheduleService } from '../../services/space-schedule.service';
import { Espacio, EspacioImage, EspacioExtra, SpaceSchedule, CreateScheduleRequest, BatchScheduleRequest, StandardWeekdayRequest, CreateEspacioRequest, SpacePricingTier } from '../../models/sala.model';
import { DialogService } from '../../services/dialog.service';
import { NotificationService } from '../../services/notification.service';
import { PlatformSettingsService } from '../../services/platform-settings.service';
import { ActionCooldownService } from '../../services/action-cooldown.service';
import { DateTimePickerDirective } from '../../directives/date-time-picker.directive';
import { GoogleLocationPickerComponent, SpaceMapLocation } from '../ui/google-location-picker.component';
import { CategoryAutocompleteComponent } from '../ui/category-autocomplete.component';
import { LocationAutocompleteComponent } from '../ui/location-autocomplete.component';
import { CooldownBubbleDirective } from '../../directives/cooldown-bubble.directive';
import { SpaceFeaturesEditorComponent } from '../space-features-editor/space-features-editor.component';
import { SpaceExtrasEditorComponent } from '../space-extras-editor/space-extras-editor.component';
import { EL_SALVADOR_DEPARTMENTS, citiesForDepartment } from '../../data/el-salvador-locations';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { environment } from '../../../environments/environment';
import { validateAndFocusFirstError, firstInvalidControlName, halfHourIncrementValidator } from '../../utils/form-validation';
import { IMAGE_UPLOAD_ACCEPT, validateSpaceImages } from '../../utils/image-upload';
import { PhotoTipsComponent } from '../ui/photo-tips.component';
import { AvailabilityBlocksComponent } from '../availability-blocks/availability-blocks.component';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-sala-detalle',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, DateTimePickerDirective, GoogleLocationPickerComponent, CategoryAutocompleteComponent, LocationAutocompleteComponent, CooldownBubbleDirective, SpaceFeaturesEditorComponent, SpaceExtrasEditorComponent, PhotoTipsComponent, AvailabilityBlocksComponent],
  template: `
    <div class="detalle-container">
      <header class="detalle-header">
        <button (click)="goBack()" class="back-btn">
          <i class="bi bi-arrow-left" aria-hidden="true"></i>
          Volver
        </button>
        <h1>Detalle del Espacio</h1>
        <div></div>
      </header>

      <div *ngIf="!espacio()" class="loading">
        <p>Cargando...</p>
      </div>

      <div *ngIf="espacio()" class="detalle-content">
        <!-- Input oculto para subir imágenes -->
        <input 
          type="file" 
          #fileInput
          class="visually-hidden-control"
          [attr.accept]="imageAccept"
          (change)="onFileSelected($event)"
          multiple
        />
        
        <!-- Imagen principal y galería -->
        <div class="images-section">
          <div class="main-image">
            <img 
              *ngIf="imagenActual()"
              [src]="getImageUrl(imagenActual()!)" 
              [alt]="espacio()?.title"
              (error)="handleImageError($event)"
            />
            <button *ngIf="imagenActual()" type="button" class="expand-image-btn" (click)="expandCurrentImage()"><i class="bi bi-arrows-fullscreen"></i> Ampliar</button>
            <span *ngIf="currentImage()?.is_featured" class="featured-main-badge" aria-label="Imagen de portada">
              <i class="bi bi-star-fill" aria-hidden="true"></i> Imagen de portada
            </span>
            <div *ngIf="!imagenActual()" class="no-image">
              <i class="bi bi-image" aria-hidden="true"></i>
              <span>Sin imagen</span>
              <button class="btn-upload" (click)="triggerFileInput()">
                <i class="bi bi-cloud-arrow-up" aria-hidden="true"></i>
                Subir imagen
              </button>
            </div>
              <button (click)="deleteImage()" *ngIf="imagenActual() && currentImage() && !isDeletingImage()" class="delete-image-btn" cooldownBubble data-cooldown-key="sala-delete-image">
              <i class="bi bi-trash3" aria-hidden="true"></i>
              Eliminar
            </button>
            <div *ngIf="isDeletingImage()" class="delete-image-btn loading">
              <div class="spinner"></div>
              Eliminando...
            </div>
          </div>
          
          <div class="image-gallery" *ngIf="espacio()?.images && espacio()!.images.length > 0">
            <div 
              *ngFor="let image of espacio()!.images; let i = index" 
              class="gallery-thumb"
              [class.active]="currentImageIndex() === i"
              (click)="selectImage(i)"
            >
              <img [src]="getImageUrl(image.image_url)" [alt]="'Imagen ' + (i + 1)" />
              <button
                type="button"
                class="feature-thumb-btn"
                [class.featured]="image.is_featured"
                [disabled]="image.is_featured || isSavingFeatured()"
                [attr.aria-label]="image.is_featured ? 'Imagen destacada actual' : 'Usar como imagen destacada'"
                [title]="image.is_featured ? 'Imagen de portada' : 'Marcar como portada'"
                (click)="setFeaturedImage(image); $event.stopPropagation()"
              ><i class="bi bi-star-fill" aria-hidden="true"></i></button>
              <button 
                (click)="deleteImageFromGallery(image); $event.stopPropagation()" 
                class="remove-thumb-btn"
                [disabled]="isDeletingImage(image)"
              >
                <i *ngIf="!isDeletingImage(image)" class="bi bi-x-lg" aria-hidden="true"></i>
                <div *ngIf="isDeletingImage(image)" class="mini-spinner"></div>
              </button>
            </div>
            <button class="add-image-btn" (click)="addImage()">
              <i class="bi bi-plus-lg" aria-hidden="true"></i>
              Agregar imagen
            </button>
          </div>

          <!-- Botón para subir imágenes cuando no hay galería -->
          <div *ngIf="!espacio()?.images || espacio()!.images.length === 0" class="upload-section">
            <button class="btn-upload-large" (click)="triggerFileInput()" [disabled]="uploadingImages()">
              <i class="bi bi-cloud-arrow-up" aria-hidden="true"></i>
              <span *ngIf="!uploadingImages()">Subir imágenes al espacio</span>
              <span *ngIf="uploadingImages()">Subiendo imágenes...</span>
            </button>
          </div>
          <div class="photo-tips-row"><app-photo-tips kind="space"></app-photo-tips></div>
        </div>

        <!-- Información principal -->
        <div class="info-section">
          <div class="section-header">
            <h2>Información del Espacio</h2>
            <button class="btn-primary" (click)="editInfo()">Editar información</button>
          </div>

          <div class="share-link-bar" *ngIf="publicSpaceUrl() as shareUrl">
            <div class="share-link-info">
              <i class="bi bi-link-45deg" aria-hidden="true"></i>
              <div>
                <small>ENLACE PÚBLICO DEL ESPACIO</small>
                <span>{{ shareUrl }}</span>
              </div>
            </div>
            <button type="button" class="share-link-copy" (click)="copyPublicSpaceUrl()">
              <i class="bi" [class.bi-clipboard]="!linkCopied()" [class.bi-clipboard-check-fill]="linkCopied()" aria-hidden="true"></i>
              {{ linkCopied() ? 'Copiado' : 'Copiar enlace' }}
            </button>
          </div>

          <div class="info-grid">
            <div class="info-item info-title">
              <label>Título</label>
              <span>{{ espacio()?.title }}</span>
            </div>

            <div class="info-item info-categories">
              <label>Categoría</label>
              <ul class="category-list"><li *ngFor="let category of categoryLabelList()"><i class="bi bi-check2"></i>{{ category }}</li></ul>
            </div>

            <div class="info-item info-description">
              <label>Descripción</label>
              <span>{{ espacio()?.description }}</span>
            </div>

            <div class="info-item info-capacity">
              <label>Capacidad</label>
              <span>{{ espacio()?.capacity }} personas</span>
            </div>

            <div class="info-item info-price">
              <label>Precio por hora</label>
              <span class="price">\${{ espacio()?.price_per_hour }}</span>
            </div>

            <div class="info-item info-address">
              <label>Ubicación</label>
              <span>{{ espacio()?.address_line1 }}</span>
            </div>

            <div class="info-item info-location">
              <label>Ciudad</label>
              <span>{{ espacio()?.city }}, {{ espacio()?.department }}, {{ espacio()?.country }}</span>
            </div>
          </div>

          <div class="pricing-tiers-summary" *ngIf="pricingTiers().length > 0">
            <label>Tarifas escalonadas</label>
            <ul class="tier-list">
              <li *ngFor="let tier of pricingTiers()" class="tier-row">
                <span class="tier-range">{{ tier.min_hours }}–{{ tier.max_hours }} horas</span>
                <span class="tier-price">\${{ tier.total_price }}</span>
              </li>
            </ul>
          </div>

          <section class="admin-space-map" *ngIf="mapEmbedUrl() as mapUrl" aria-labelledby="admin-space-map-title">
            <header>
              <div><small>UBICACIÓN REGISTRADA</small><h3 id="admin-space-map-title">Punto exacto del espacio</h3><p>{{ espacio()?.address_line1 }}, {{ espacio()?.city }}, {{ espacio()?.department }}, {{ espacio()?.country }}</p></div>
              <a [href]="mapDirectionsUrl()" target="_blank" rel="noopener noreferrer"><i class="bi bi-sign-turn-right"></i> Abrir en Google Maps</a>
            </header>
            <iframe [src]="mapUrl" loading="lazy" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen title="Mapa de ubicación del espacio"></iframe>
          </section>
        </div>

        <!-- Características incluidas (sin costo, con icono) -->
        <app-space-features-editor [spaceId]="espacio()!.space_id"></app-space-features-editor>

        <!-- Extras (servicios con costo): icono, precio, máximo por reserva -->
        <app-space-extras-editor [spaceId]="espacio()!.space_id" (extrasChange)="onExtrasChange($event)"></app-space-extras-editor>

        <!-- Horarios -->
        <div class="schedules-section">
          <div class="section-header">
            <h2>Horarios de Disponibilidad</h2>
            <div class="schedule-actions">
              <button class="btn-secondary" (click)="setStandardWeekday()" [disabled]="isSavingSchedules()">
                Configurar Lunes-Viernes
              </button>
              <button class="btn-primary" (click)="openScheduleModal()">
                <i class="bi bi-plus-lg" aria-hidden="true"></i>
                Configurar Horarios
              </button>
            </div>
          </div>

          <div *ngIf="visibleSchedules().length === 0" class="empty-schedules">
            <p>No hay horarios configurados. El espacio estará disponible 24/7.</p>
            <small>Configura horarios para controlar cuándo el espacio está disponible para reservas.</small>
          </div>

          <div *ngIf="visibleSchedules().length > 0" class="schedules-table-container">
            <table class="schedules-table">
              <thead>
                <tr>
                  <th>Día</th>
                  <th>Hora de Apertura</th>
                  <th>Hora de Cierre</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let schedule of visibleSchedules()">
                  <td class="schedule-day" data-label="Día"><strong>{{ spaceScheduleService.getDayName(schedule.day_of_week) }}</strong></td>
                  <td class="schedule-opening" data-label="Apertura">{{ spaceScheduleService.formatDisplayTime(schedule.opening_time) }}</td>
                  <td class="schedule-closing" data-label="Cierre">{{ spaceScheduleService.formatDisplayTime(schedule.closing_time) }}</td>
                  <td class="schedule-status" data-label="Estado">
                    <span [class]="'status-' + (schedule.is_closed ? 'closed' : 'open')">
                      {{ schedule.is_closed ? 'Cerrado' : 'Abierto' }}
                    </span>
                  </td>
                  <td class="actions-cell" data-label="Acciones">
                    <button
                      (click)="editSchedule(schedule)"
                      class="btn-action btn-edit-small"
                      title="Editar"
                      aria-label="Editar horario"
                    >
                      <i class="bi bi-pencil-square"></i>
                    </button>
                    <button
                      (click)="requestDeleteSchedule(schedule)"
                      class="btn-action btn-delete"
                      title="Eliminar"
                      aria-label="Eliminar horario"
                    >
                      <i class="bi bi-trash3"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Días no disponibles (asueto, mantenimiento...): del espacio y del negocio completo. -->
        <app-availability-blocks class="availability-blocks-section" [spaceId]="espacio()!.space_id" [canEdit]="canEditBlocks"></app-availability-blocks>

        <!-- Acciones -->
        <div class="actions-section">
          <button class="btn-danger" (click)="deleteEspacio()">
            <i class="bi bi-trash3" aria-hidden="true"></i>
            Eliminar Espacio
          </button>
        </div>
      </div>
    </div>

    <div *ngIf="expandedImage() as image" class="image-lightbox" (click)="expandedImage.set(null)" role="dialog" aria-modal="true" aria-label="Vista ampliada de la imagen">
      <button type="button" class="lightbox-close" (click)="expandedImage.set(null)">×</button><img [src]="image" [alt]="espacio()?.title || 'Imagen del espacio'" (click)="$event.stopPropagation()" />
    </div>


    <!-- Modal de Editar Información -->
    <div *ngIf="showEditModal()" class="modal-overlay" (click)="closeEditModal()">
      <div class="modal-content modal-wide" (click)="$event.stopPropagation()">
        <div class="modal-header">
          <h2>Editar Información del Espacio</h2>
          <button class="close-btn" (click)="closeEditModal()">×</button>
        </div>

        <form #editFormRef [formGroup]="editForm" (ngSubmit)="saveEditInfo()" class="modal-body">

          <nav class="tab-nav" role="tablist" aria-label="Secciones de edición">
            <button type="button" role="tab" [attr.aria-selected]="activeEditTab() === 'general'" [class.active]="activeEditTab() === 'general'" (click)="activeEditTab.set('general')">
              <i class="bi bi-card-text"></i> General
            </button>
            <button type="button" role="tab" [attr.aria-selected]="activeEditTab() === 'precio'" [class.active]="activeEditTab() === 'precio'" (click)="activeEditTab.set('precio')">
              <i class="bi bi-cash-coin"></i> Capacidad y precios
            </button>
            <button type="button" role="tab" [attr.aria-selected]="activeEditTab() === 'ubicacion'" [class.active]="activeEditTab() === 'ubicacion'" (click)="activeEditTab.set('ubicacion')">
              <i class="bi bi-geo-alt"></i> Ubicación
            </button>
          </nav>

          <div class="tab-panel" [hidden]="activeEditTab() !== 'general'" [class.tab-panel-hidden]="activeEditTab() !== 'general'">
            <div class="form-group">
              <label>Título *</label>
              <input placeholder="Ej: Sala Ejecutiva Cumbre" type="text" formControlName="title" class="form-control" maxlength="100" />
              <small *ngIf="editForm.get('title')?.hasError('required')" class="error-text">El título es requerido</small>
            </div>

            <div class="form-group">
              <label>Descripción *</label>
              <textarea placeholder="Describe el espacio: equipamiento, ambiente y para qué actividades es ideal" formControlName="description" class="form-control" rows="4" maxlength="2000"></textarea>
              <small *ngIf="editForm.get('description')?.hasError('required')" class="error-text">La descripción es requerida</small>
            </div>

            <div class="form-group category-field">
              <label>Categorías * <small>(busca y selecciona hasta 8)</small></label>
              <app-category-autocomplete
                #categoryAc
                [options]="categories"
                [selected]="editForm.get('categories')?.value || []"
                [max]="8"
                [invalid]="!!(editForm.get('categories')?.hasError('required') && editForm.get('categories')?.touched)"
                (selectedChange)="setEditCategories($event)"
              ></app-category-autocomplete>
              <small *ngIf="editForm.get('categories')?.hasError('required') && editForm.get('categories')?.touched" class="error-text">Selecciona al menos una categoría</small>
            </div>

            <div class="form-group status-field">
              <label class="checkbox-label">
                <input type="checkbox" formControlName="is_active" />
                <span>Espacio activo y disponible</span>
              </label>
            </div>
          </div>

          <div class="tab-panel" [hidden]="activeEditTab() !== 'precio'" [class.tab-panel-hidden]="activeEditTab() !== 'precio'">
            <div class="form-row metrics-row">
              <div class="form-group">
                <label>Capacidad *</label>
                <input placeholder="Ej: 12" type="number" formControlName="capacity" class="form-control" min="1" />
                <small *ngIf="editForm.get('capacity')?.hasError('required')" class="error-text">La capacidad es requerida</small>
              </div>

              <div class="form-group">
                <label>Precio por hora *</label>
                <input placeholder="Ej: 25.00" type="number" formControlName="price_per_hour" class="form-control" step="0.01" min="0" />
                <small *ngIf="editForm.get('price_per_hour')?.hasError('required')" class="error-text">El precio es requerido</small>
              </div>
            </div>
            <p class="tab-hint"><i class="bi bi-info-circle"></i> TuSala cobra una comisión de {{ commissionRate() }}% sobre el total facturado.
              <span *ngIf="netPricePerHour() > 0"> Con este precio recibirías <strong>{{ netPricePerHour() | currency:'USD' }}</strong> por hora, ya con la comisión descontada.</span>
            </p>

            <div class="pricing-tiers-block">
              <h3>Tarifas escalonadas <span class="tier-optional">(opcional)</span></h3>
              <p class="tab-hint"><i class="bi bi-info-circle"></i> Define precios fijos para rangos de horas (ej: 2-4 horas por $15) en vez de cobrar siempre por hora. Si una reserva no cae en ningún rango, se usa el precio por hora normal.</p>

              <div *ngIf="loadingTiers()" class="tiers-loading">Cargando tarifas…</div>
              <div *ngIf="!loadingTiers() && pricingTiers().length === 0" class="empty-tiers">No hay tarifas escalonadas configuradas todavía.</div>

              <ul *ngIf="!loadingTiers() && pricingTiers().length > 0" class="tier-list">
                <li *ngFor="let tier of pricingTiers()" class="tier-row">
                  <span class="tier-range">{{ tier.min_hours }}–{{ tier.max_hours }} horas</span>
                  <span class="tier-price">\${{ tier.total_price }}</span>
                  <button type="button" class="tier-delete-btn" (click)="deletePricingTier(tier)"
                    [disabled]="isDeletingTier(tier)" [attr.aria-label]="'Eliminar tarifa de ' + tier.min_hours + ' a ' + tier.max_hours + ' horas'">
                    <div *ngIf="!isDeletingTier(tier)"><i class="bi bi-trash3" aria-hidden="true"></i></div>
                    <div *ngIf="isDeletingTier(tier)" class="mini-spinner"></div>
                  </button>
                </li>
              </ul>

              <div class="tier-add-form" [formGroup]="tierForm">
                <div class="form-group">
                  <label>Desde (horas)</label>
                  <input type="number" formControlName="min_hours" class="form-control" min="1" step="0.5" placeholder="2" />
                  <small class="error-text" *ngIf="tierForm.get('min_hours')?.hasError('halfHourIncrement') && tierForm.get('min_hours')?.touched">Solo intevalos de media hora(0.5)</small>
                </div>
                <div class="form-group">
                  <label>Hasta (horas)</label>
                  <input type="number" formControlName="max_hours" class="form-control" min="1" step="0.5" placeholder="4" />
                  <small class="error-text" *ngIf="tierForm.get('max_hours')?.hasError('halfHourIncrement') && tierForm.get('max_hours')?.touched">Solo intevalos de media hora(0.5)</small>
                </div>
                <div class="form-group">
                  <label>Precio total (USD)</label>
                  <input type="number" formControlName="total_price" class="form-control" step="0.01" min="0" placeholder="15.00" />
                </div>
                <div class="form-group tier-add-btn-wrap">
                  <label aria-hidden="true">&nbsp;</label>
                  <button type="button" class="btn-secondary tier-add-btn" (click)="addPricingTier()" [disabled]="addingTier()">
                    <i class="bi bi-plus-lg" aria-hidden="true"></i> {{ addingTier() ? 'Agregando…' : 'Agregar tarifa' }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="tab-panel" [hidden]="activeEditTab() !== 'ubicacion'" [class.tab-panel-hidden]="activeEditTab() !== 'ubicacion'">
            <div class="form-row location-row">
              <div class="form-group">
                <label>País *</label>
                <input placeholder="Ej: El Salvador" type="text" formControlName="country" class="form-control" maxlength="100" />
                <small *ngIf="editForm.get('country')?.hasError('required')" class="error-text">El país es requerido</small>
              </div>

              <div class="form-group">
                <label>Departamento *</label>
                <app-location-autocomplete
                  [options]="departments"
                  [value]="editForm.get('department')?.value || ''"
                  [allowCustom]="false"
                  placeholder="Busca un departamento"
                  [invalid]="!!(editForm.get('department')?.hasError('required') && editForm.get('department')?.touched)"
                  (valueChange)="setEditDepartment($event)"
                ></app-location-autocomplete>
                <small *ngIf="editForm.get('department')?.hasError('required')" class="error-text">El departamento es requerido</small>
              </div>

              <div class="form-group">
                <label>Ciudad *</label>
                <app-location-autocomplete
                  [options]="citiesForSelectedEditDepartment()"
                  [value]="editForm.get('city')?.value || ''"
                  [allowCustom]="true"
                  [disabled]="!editForm.get('department')?.value"
                  [placeholder]="editForm.get('department')?.value ? 'Busca o escribe una ciudad' : 'Primero selecciona un departamento'"
                  [invalid]="!!(editForm.get('city')?.hasError('required') && editForm.get('city')?.touched)"
                  (valueChange)="setEditCity($event)"
                ></app-location-autocomplete>
                <small *ngIf="editForm.get('city')?.hasError('required')" class="error-text">La ciudad es requerida</small>
              </div>
            </div>

            <div class="form-group">
              <label>Dirección *</label>
              <input placeholder="Ej: Calle La Reforma #123, Col. San Benito" type="text" formControlName="address_line1" class="form-control" maxlength="255" />
              <small *ngIf="editForm.get('address_line1')?.hasError('required')" class="error-text">La dirección es requerida</small>
            </div>

            <app-google-location-picker
              [address]="editFullAddress"
              [latitude]="editForm.get('latitude')?.value"
              [longitude]="editForm.get('longitude')?.value"
              (locationChange)="setEditMapLocation($event)" />
          </div>

          <div class="modal-actions">
            <button type="button" class="btn-cancel" (click)="closeEditModal()">Cancelar</button>
            <button type="submit" class="btn-save" cooldownBubble data-cooldown-key="sala-save-details">Guardar Cambios</button>
          </div>
        </form>
      </div>
    </div>


    <!-- Modal de Configurar Horarios: un renglón por día, selector de hora del
         panel (solo en punto o y media) y validación al guardar. -->
    <div *ngIf="showScheduleModal()" class="modal-overlay" (click)="closeScheduleModal()">
      <div class="modal-content modal-large sched-modal" (click)="$event.stopPropagation()" role="dialog" aria-modal="true" aria-labelledby="sched-title">
        <div class="modal-header">
          <h2 id="sched-title">Configurar horarios</h2>
          <button type="button" class="close-btn" (click)="closeScheduleModal()" aria-label="Cerrar">×</button>
        </div>

        <form [formGroup]="scheduleForm" (ngSubmit)="saveSchedules()" class="modal-body sched-body" novalidate>
          <p class="sched-intro"><i class="bi bi-info-circle" aria-hidden="true"></i> Marca los días de atención y su horario. Las horas van en punto o y media (ej. 8:00 u 8:30). Un día cerrado no se puede reservar.</p>
          <div class="sched-presets" aria-label="Atajos">
            <span>Atajos:</span>
            <button type="button" class="sched-chip" (click)="applySchedulePreset('weekdays')">Lunes a viernes · 8:00–17:00</button>
            <button type="button" class="sched-chip" (click)="applySchedulePreset('all')">Todos los días · 8:00–17:00</button>
            <button type="button" class="sched-chip" (click)="copyFirstOpenScheduleDay()">Copiar el primer día abierto</button>
          </div>

          <div class="sched-days">
            <div *ngFor="let day of daysOfWeek; let i = index; trackBy: trackScheduleDay" class="sched-day"
              [class.closed]="isScheduleDayClosed(i)" [class.invalid]="scheduleSubmitted() && scheduleDayError(i)">
              <label class="sched-switch">
                <input type="checkbox" [checked]="!isScheduleDayClosed(i)" (change)="setScheduleDayOpen(i, $any($event.target).checked)" />
                <span>{{ day.name }}</span>
              </label>
              <div class="sched-times" *ngIf="!isScheduleDayClosed(i)">
                <input type="text" appDatePicker="time" [formControlName]="'day_' + i + '_opening'" class="form-control sched-time" placeholder="Apertura" [attr.aria-label]="'Apertura ' + day.name" />
                <span aria-hidden="true">a</span>
                <input type="text" appDatePicker="time" [formControlName]="'day_' + i + '_closing'" class="form-control sched-time" placeholder="Cierre"
                  [afterTime]="scheduleForm.get('day_' + i + '_opening')?.value" afterTimeMessage="El cierre debe ser después de la apertura" [attr.aria-label]="'Cierre ' + day.name" />
              </div>
              <span class="sched-closed-label" *ngIf="isScheduleDayClosed(i)">Cerrado</span>
              <small class="sched-err" *ngIf="scheduleSubmitted() && scheduleDayError(i) as message">{{ message }}</small>
            </div>
          </div>
          <p class="sched-err sched-err-block" *ngIf="scheduleSubmitted() && allScheduleDaysClosed()" role="alert">Deja al menos un día abierto. Si quieres que se pueda reservar siempre, elimina los horarios.</p>

          <div class="modal-actions">
            <button type="button" class="btn-cancel" (click)="closeScheduleModal()">Cancelar</button>
            <button type="submit" class="btn-save" [disabled]="isSavingSchedules()" cooldownBubble data-cooldown-key="sala-save-schedules">
              {{ isSavingSchedules() ? 'Guardando...' : 'Guardar horarios' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Modal de Editar Horario Individual -->
    <div *ngIf="editingSchedule()" class="modal-overlay" (click)="closeEditScheduleModal()">
      <div class="modal-content schedule-edit-modal" (click)="$event.stopPropagation()">
        <div class="modal-header">
          <h2>Editar Horario - {{ editingSchedule() ? spaceScheduleService.getDayName(editingSchedule()!.day_of_week) : '' }}</h2>
          <button class="close-btn" (click)="closeEditScheduleModal()">×</button>
        </div>

        <form #editScheduleFormRef [formGroup]="editScheduleForm" (ngSubmit)="saveEditSchedule()" class="modal-body">
          <div class="form-group">
            <label class="checkbox-label">
              <input type="checkbox" formControlName="is_closed" />
              <span>Cerrado</span>
            </label>
          </div>

          <div class="form-row" [class.closed]="editScheduleForm.get('is_closed')?.value">
            <div class="form-group">
              <label><i class="bi bi-clock" aria-hidden="true"></i> Hora de Apertura</label>
              <input
                type="text"
                appDatePicker="time"
                formControlName="opening_time"
                class="form-control"
                placeholder="Seleccionar apertura"
                [pickerDisabled]="!!editScheduleForm.get('is_closed')?.value"
              />
            </div>
            <div class="form-group">
              <label><i class="bi bi-clock" aria-hidden="true"></i> Hora de Cierre</label>
              <input
                type="text"
                appDatePicker="time"
                formControlName="closing_time"
                  [afterTime]="editScheduleForm.get('opening_time')?.value"
                  afterTimeMessage="La hora de cierre debe ser posterior a la de apertura"
                class="form-control"
                placeholder="Seleccionar cierre"
                [pickerDisabled]="!!editScheduleForm.get('is_closed')?.value"
              />
            </div>
          </div>

          <div class="modal-actions">
            <button type="button" class="btn-cancel" (click)="closeEditScheduleModal()">Cancelar</button>
            <button type="submit" class="btn-save" [disabled]="isSavingSchedule()">
              {{ isSavingSchedule() ? 'Guardando...' : 'Guardar Cambios' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Notificación -->
    <div *ngIf="schedulePendingDeletion() as schedule" class="modal-overlay schedule-delete-overlay" (click)="closeDeleteScheduleModal()">
      <section class="schedule-delete-modal" role="alertdialog" aria-modal="true" aria-labelledby="deleteScheduleTitle" (click)="$event.stopPropagation()">
        <button type="button" class="schedule-delete-close" (click)="closeDeleteScheduleModal()" aria-label="Cerrar">×</button>
        <span class="schedule-delete-icon"><i class="bi bi-calendar2-x" aria-hidden="true"></i></span>
        <span class="schedule-delete-label">Acción importante</span>
        <h2 id="deleteScheduleTitle">Eliminar horario de {{ spaceScheduleService.getDayName(schedule.day_of_week) }}</h2>
        <p>Este día dejará de usar el intervalo configurado en la disponibilidad del espacio.</p>
        <div class="schedule-delete-summary">
          <span><small>Apertura</small><strong>{{ spaceScheduleService.formatDisplayTime(schedule.opening_time) }}</strong></span>
          <i class="bi bi-arrow-right" aria-hidden="true"></i>
          <span><small>Cierre</small><strong>{{ spaceScheduleService.formatDisplayTime(schedule.closing_time) }}</strong></span>
          <span class="schedule-delete-state"><small>Estado actual</small><strong>{{ schedule.is_closed ? 'Cerrado' : 'Abierto' }}</strong></span>
        </div>
        <div class="schedule-delete-warning"><i class="bi bi-exclamation-triangle" aria-hidden="true"></i><span>La disponibilidad de este día cambiará y podría afectar la creación de nuevas reservas.</span></div>
        <div class="schedule-delete-actions">
          <button type="button" class="btn-cancel" (click)="closeDeleteScheduleModal()">Conservar horario</button>
          <button type="button" class="btn-confirm-delete" (click)="confirmDeleteSchedule()" [disabled]="deletingSchedule()"><i class="bi bi-trash3" aria-hidden="true"></i>{{ deletingSchedule() ? 'Eliminando...' : 'Eliminar horario' }}</button>
        </div>
      </section>
    </div>

  `,
  styles: [`
    .detalle-container {
      min-height: 100vh;
      background: var(--ts-bg-gray-50);
    }

    .detalle-header {
      background: var(--ts-bg);
      color: white;
      padding: 20px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .detalle-header h1 {
      margin: 0;
      font-size: 24px;
      font-weight: 700;
    }

    .back-btn {
      background: white;
      color: var(--ts-bg);
      border: none;
      padding: 8px 16px;
      border-radius: 6px;
      cursor: pointer;
      font-weight: 600;
      display: flex;
      align-items: center;
      gap: 8px;
      transition: all 0.3s ease;
    }

    .back-btn:hover {
      background: var(--ts-bg-gray-100);
    }

    .back-btn svg {
      flex-shrink: 0;
    }

    .loading {
      text-align: center;
      padding: 100px 20px;
    }

    .detalle-content {
      max-width: 1200px;
      margin: 0 auto;
      padding: 40px 20px;
    }

    .images-section {
      margin-bottom: 40px;
    }

    .availability-blocks-section { display: block; margin: 0 0 24px; }

    .photo-tips-row { margin-top: 10px; }

    .main-image {
      position: relative;
      width: 100%;
      height: 500px;
      border-radius: 12px;
      overflow: hidden;
      background: var(--ts-bg-gray-50);
      margin-bottom: 20px;
    }

    /* contain: el dueño ve la foto completa tal como la subió (en la ficha
       pública las verticales también se muestran completas). */
    .main-image img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }

    .no-image {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100%;
      color: var(--ts-text-gray-400);
      gap: 16px;
    }

    .btn-upload {
      background: var(--ts-bg);
      color: white;
      border: none;
      padding: 12px 24px;
      border-radius: 8px;
      cursor: pointer;
      font-weight: 600;
      display: flex;
      align-items: center;
      gap: 8px;
      transition: all 0.3s ease;
      margin-top: 16px;
    }

    .btn-upload:hover {
      background: var(--ts-bg-gray-800);
    }

    .upload-section {
      margin-top: 24px;
      display: flex;
      justify-content: center;
    }

    .btn-upload-large {
      background: var(--ts-bg);
      color: white;
      border: none;
      padding: 24px 48px;
      border-radius: 12px;
      cursor: pointer;
      font-weight: 600;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 16px;
      transition: all 0.3s ease;
      min-width: 300px;
    }

    .btn-upload-large:hover:not(:disabled) {
      background: var(--ts-bg-gray-800);
    }

    .btn-upload-large:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .delete-image-btn {
      position: absolute;
      bottom: 16px;
      right: 16px;
      background: var(--ts-bg-red-400-a90);
      color: white;
      border: none;
      padding: 10px 16px;
      border-radius: 6px;
      cursor: pointer;
      font-weight: 600;
      display: flex;
      align-items: center;
      gap: 8px;
      transition: all 0.3s ease;
    }

    .delete-image-btn:hover:not(.loading) {
      background: var(--ts-bg-red-400);
    }

    .delete-image-btn.loading {
      opacity: 0.7;
      cursor: not-allowed;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .spinner {
      width: 16px;
      height: 16px;
      border: 2px solid var(--ts-border-gray-0-a30);
      border-top-color: white;
      border-radius: 50%;
      animation: spin 0.6s linear infinite;
    }

    .mini-spinner {
      width: 12px;
      height: 12px;
      border: 2px solid currentColor;
      border-top-color: transparent;
      border-radius: 50%;
      animation: spin 0.6s linear infinite;
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    .image-gallery {
      display: flex;
      gap: 12px;
      overflow-x: auto;
      padding-bottom: 10px;
    }

    .gallery-thumb {
      position: relative;
      width: 100px;
      height: 100px;
      border-radius: 8px;
      overflow: hidden;
      flex-shrink: 0;
      cursor: pointer;
      border: 3px solid transparent;
      transition: all 0.3s ease;
    }

    .gallery-thumb.active {
      border-color: var(--ts-bg);
    }

    .gallery-thumb img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .remove-thumb-btn {
      position: absolute;
      top: 4px;
      right: 4px;
      width: 24px;
      height: 24px;
      padding: 0;
      border-radius: 50%;
      /* Casi opaco a propósito -- antes un estilo global pensado para
         botones de texto (min-height/padding/border-radius/font-size,
         además de un color/fondo "danger" muy tenue) se aplicaba también
         aquí y volvía la "×" apenas visible sobre fotos claras. Ver el
         mismo ajuste en .feature-thumb-btn. */
      background: var(--ts-bg-red-500-a94);
      color: var(--ts-text-on-accent);
      border: 1px solid var(--ts-border-gray-0-a90);
      box-shadow: 0 2px 6px var(--ts-shadow-gray-1000-a35);
      cursor: pointer;
      font-size: 13px;
      line-height: 1;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .remove-thumb-btn:hover:not(:disabled) {
      background: var(--ts-bg-red-550);
    }

    .add-image-btn {
      width: 100px;
      height: 100px;
      border: 2px dashed var(--ts-border-gray-200);
      border-radius: 8px;
      background: white;
      cursor: pointer;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 8px;
      color: var(--ts-text-gray-600);
      font-weight: 600;
      transition: all 0.3s ease;
      flex-shrink: 0;
    }

    .add-image-btn:hover {
      border-color: var(--ts-bg);
      background: var(--ts-bg-gray-0-2);
    }

    .info-section, .extras-section, .schedules-section, .actions-section {
      background: white;
      border-radius: 12px;
      padding: 30px;
      margin-bottom: 24px;
    }

    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
    }

    .section-header h2 {
      margin: 0;
      font-size: 20px;
      font-weight: 700;
      color: var(--ts-text-gray-800);
    }

    .info-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 24px;
    }

    .info-item {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .info-item label {
      font-size: 12px;
      font-weight: 600;
      color: var(--ts-text-gray-600);
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .info-item span {
      font-size: 16px;
      color: var(--ts-text-gray-800);
    }

    .price {
      font-size: 24px;
      font-weight: 700;
      color: var(--ts-text-green-550);
    }

    .status-badge {
      display: inline-block;
      padding: 6px 12px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 600;
      width: fit-content;
    }

    .status-badge.active {
      background: var(--ts-bg-green-50);
      color: var(--ts-text-green-550);
    }

    .status-badge.unavailable {
      background: var(--ts-bg-red-50);
      color: var(--ts-text-red-400);
    }

    .btn-toggle-status {
      margin-left: 12px;
      padding: 6px 16px;
      border: 1px solid var(--ts-border-blue-150-3);
      border-radius: 8px;
      background: var(--ts-text);
      color: var(--ts-text-navy-750-4);
      font-size: 12px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s;
    }

    .btn-toggle-status:hover {
      background: var(--ts-bg-blue-50-13);
      border-color: var(--ts-border-gray-350);
    }

    .extras-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
      gap: 16px;
    }

    .extra-card {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 20px;
      border: 2px solid var(--ts-border-gray-100);
      border-radius: 8px;
      transition: all 0.3s ease;
    }

    .extra-card:hover {
      border-color: var(--ts-bg);
      background: var(--ts-bg-gray-0-2);
    }

    .extra-info h3 {
      margin: 0 0 4px 0;
      font-size: 16px;
      font-weight: 700;
      color: var(--ts-text-gray-800);
    }

    .extra-info p {
      margin: 0 0 8px 0;
      font-size: 14px;
      color: var(--ts-text-gray-600);
    }

    .extra-price {
      font-size: 18px;
      font-weight: 700;
      color: var(--ts-text-green-550);
    }

    .delete-extra-btn {
      background: var(--ts-bg-red-50);
      color: var(--ts-text-red-400);
      border: 2px solid var(--ts-border-red-100);
      width: 40px;
      height: 40px;
      border-radius: 8px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      flex-shrink: 0;
    }

    .delete-extra-btn:hover:not(:disabled) {
      background: var(--ts-bg-red-50-8);
      border-color: var(--ts-border-red-400);
    }

    .delete-extra-btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .remove-thumb-btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .empty-extras {
      text-align: center;
      padding: 40px 20px;
      color: var(--ts-text-gray-400);
    }

    .btn-primary {
      background: var(--ts-bg);
      color: white;
      border: none;
      padding: 10px 20px;
      border-radius: 6px;
      cursor: pointer;
      font-weight: 600;
      transition: all 0.3s ease;
    }

    .btn-primary:hover {
      background: var(--ts-bg-gray-800);
    }

    .btn-secondary {
      background: white;
      color: var(--ts-text-gray-800);
      border: 2px solid var(--ts-border-gray-100);
      padding: 10px 20px;
      border-radius: 6px;
      cursor: pointer;
      font-weight: 600;
      display: flex;
      align-items: center;
      gap: 8px;
      transition: all 0.3s ease;
    }

    .btn-secondary:hover {
      border-color: var(--ts-bg);
      background: var(--ts-bg-gray-0-2);
    }

    .btn-danger {
      background: var(--ts-bg-red-400);
      color: white;
      border: none;
      padding: 12px 24px;
      border-radius: 6px;
      cursor: pointer;
      font-weight: 600;
      display: flex;
      align-items: center;
      gap: 8px;
      transition: all 0.3s ease;
      width: 100%;
      justify-content: center;
    }

    .btn-danger:hover {
      background: var(--ts-bg-red-500);
    }

    /* Modal Styles */
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: var(--ts-bg-gray-1000-a70);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      padding: 20px;
    }

    .modal-content {
      background: white;
      border-radius: 12px;
      width: 100%;
      max-width: 600px;
      max-height: 90vh;
      overflow-y: auto;
      box-shadow: 0 10px 40px var(--ts-shadow-gray-1000-a30);
    }

    .modal-large {
      max-width: 900px;
    }

    .modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 24px;
      border-bottom: 2px solid var(--ts-border-gray-100);
    }

    .modal-header h2 {
      margin: 0;
      font-size: 20px;
      font-weight: 700;
      color: var(--ts-text-gray-800);
    }

    .close-btn {
      background: none;
      border: none;
      font-size: 32px;
      color: var(--ts-text-gray-600);
      cursor: pointer;
      line-height: 1;
      padding: 0;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: color 0.3s ease;
    }

    .close-btn:hover {
      color: var(--ts-bg);
    }

    .modal-body {
      padding: 24px;
    }

    .form-group {
      margin-bottom: 20px;
    }

    .form-group label {
      display: block;
      margin-bottom: 8px;
      font-weight: 600;
      color: var(--ts-text-gray-800);
      font-size: 14px;
    }

    .checkbox-label {
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
      font-weight: 500;
    }

    .checkbox-label input[type="checkbox"] {
      width: 18px;
      height: 18px;
      cursor: pointer;
    }

    .form-control {
      width: 100%;
      padding: 10px 14px;
      border: 2px solid var(--ts-border-gray-100);
      border-radius: 6px;
      font-size: 14px;
      transition: all 0.3s ease;
      font-family: inherit;
    }

    .form-control:focus {
      outline: none;
      border-color: var(--ts-bg);
    }

    .form-control.error {
      border-color: var(--ts-border-red-400);
    }

    .form-row {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
      gap: 16px;
    }

    .error-text {
      display: block !important;
      color: var(--ts-text-red-250-8) !important;
      font-size: 12px;
      margin-top: 4px;
      font-weight: 600;
    }


    .modal-actions {
      display: flex;
      gap: 12px;
      justify-content: flex-end;
      margin-top: 24px;
      padding-top: 24px;
      border-top: 2px solid var(--ts-border-gray-100);
    }

    .btn-cancel {
      background: white;
      color: var(--ts-text-gray-800);
      border: 2px solid var(--ts-border-gray-100);
      padding: 10px 20px;
      border-radius: 6px;
      cursor: pointer;
      font-weight: 600;
      transition: all 0.3s ease;
    }

    .btn-cancel:hover {
      border-color: var(--ts-border-gray-400);
      background: var(--ts-bg-gray-0-2);
    }

    .btn-save {
      background: var(--ts-bg);
      color: white;
      border: 2px solid var(--ts-bg);
      padding: 10px 20px;
      border-radius: 6px;
      cursor: pointer;
      font-weight: 600;
      transition: all 0.3s ease;
    }

    .btn-save:hover:not(:disabled) {
      background: var(--ts-bg-gray-800);
      border-color: var(--ts-border-gray-800);
    }

    .btn-save:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    /* Estilos para Horarios */
    .schedule-actions {
      display: flex;
      gap: 12px;
    }

    .empty-schedules {
      text-align: center;
      padding: 40px;
      color: var(--ts-text-gray-600);
    }

    .empty-schedules p {
      margin-bottom: 8px;
      font-size: 16px;
    }

    .empty-schedules small {
      color: var(--ts-text-gray-400);
      font-size: 14px;
    }

    .schedules-table-container {
      overflow-x: auto;
    }

    .schedules-table {
      width: 100%;
      border-collapse: collapse;
      min-width: 600px;
    }

    .schedules-table th {
      background: var(--ts-bg-gray-50);
      padding: 12px;
      text-align: left;
      font-weight: 600;
      color: var(--ts-text-gray-800);
      border-bottom: 2px solid var(--ts-border-gray-100);
      font-size: 14px;
    }

    .schedules-table td {
      padding: 12px;
      border-bottom: 1px solid var(--ts-border-gray-100);
      font-size: 14px;
    }

    .schedules-table tr:hover {
      background: var(--ts-bg-gray-0-2);
    }

    .status-open {
      background: var(--ts-bg-green-100);
      color: var(--ts-text-green-800);
      padding: 4px 12px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 500;
      display: inline-block;
    }

    .status-closed {
      background: var(--ts-bg-red-100);
      color: var(--ts-text-red-700);
      padding: 4px 12px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 500;
      display: inline-block;
    }

    .actions-cell {
      display: flex;
      gap: 8px;
    }

    .btn-action {
      display: grid;
      place-items: center;
      width: 32px;
      height: 32px;
      padding: 0;
      border: none;
      border-radius: 6px;
      cursor: pointer;
      font-size: 14px;
      transition: all 0.3s ease;
    }

    .btn-edit-small {
      background: var(--ts-bg);
      color: white;
    }

    .btn-edit-small:hover {
      background: var(--ts-bg-gray-800);
    }

    .btn-delete {
      background: var(--ts-bg-red-400);
      color: white;
    }

    .btn-delete:hover {
      background: var(--ts-bg-red-500);
    }

    .schedule-instructions {
      background: var(--ts-bg-gray-0-2);
      padding: 16px;
      border-radius: 8px;
      margin-bottom: 24px;
    }

    .schedule-instructions p {
      margin: 0 0 8px 0;
      font-weight: 600;
      color: var(--ts-text-gray-800);
    }

    .schedule-instructions ul {
      margin: 8px 0 0 0;
      padding-left: 20px;
      color: var(--ts-text-gray-600);
    }

    .schedule-instructions li {
      margin-bottom: 4px;
    }

    .schedules-form-list {
      display: flex;
      flex-direction: column;
      gap: 20px;
      max-height: 500px;
      overflow-y: auto;
      padding-right: 8px;
    }

    .schedule-day-form {
      border: 2px solid var(--ts-border-gray-100);
      border-radius: 8px;
      padding: 16px;
      background: var(--ts-bg-gray-0-3);
    }

    .day-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
    }

    .day-header h3 {
      margin: 0;
      font-size: 16px;
      font-weight: 700;
      color: var(--ts-text-gray-800);
    }

    .day-schedule {
      transition: opacity 0.3s ease;
    }

    .day-schedule.closed {
      opacity: 0.5;
    }

    .day-schedule.closed .form-control {
      background: var(--ts-bg-gray-50);
    }

    .detalle-container { background:var(--ts-bg-blue-50); color:var(--ts-text-navy-800-3); padding-bottom:2rem; } .detalle-header { max-width:1180px; margin:0 auto; padding:1.25rem 1.5rem; color:var(--ts-text-navy-750); background:transparent; } .detalle-header h1 { font-size:1.45rem; letter-spacing:-.03em; } .back-btn { border:1px solid var(--ts-border-sky-100-10); border-radius:9px; padding:.55rem .75rem; color:var(--ts-text-blue-550-8); background:var(--ts-text); box-shadow:0 3px 9px var(--ts-shadow-navy-750-a6); } .detalle-content { max-width:1180px; margin:0 auto; padding:0 1.5rem; } .images-section,.info-section,.extras-section,.schedules-section,.actions-section { border:1px solid var(--ts-border-sky-100-11); border-radius:16px; box-shadow:0 8px 20px var(--ts-shadow-navy-750-a6-2); } .main-image { border-radius:14px; overflow:hidden; background:var(--ts-bg-sky-50-8); } .section-header { border-bottom:1px solid var(--ts-border-blue-50); padding-bottom:1rem; } .section-header h2 { color:var(--ts-text-navy-750); } .info-item { border-radius:10px; background:var(--ts-bg-blue-0-3); } .modal-content,.modal-large { border-radius:16px; border:1px solid var(--ts-border-sky-100-2); box-shadow:0 26px 72px var(--ts-shadow-navy-900-a28); }



    /* Capa visual profesional para la ficha y todos sus submodales. */
    .detalle-container{background:linear-gradient(180deg,var(--ts-bg-blue-50-11) 0,var(--ts-bg-blue-0-4) 220px,var(--ts-bg-blue-0-4) 100%)}
    .detalle-header{position:static;max-width:1220px;padding:.85rem 1.5rem;margin:0 auto;background:transparent;border-bottom:0;backdrop-filter:none}
    .detalle-header h1{color:var(--ts-text-navy-800-4);font-size:1.3rem}.detalle-content{max-width:1220px;padding:1.5rem}
    .images-section{position:relative;padding:1rem;margin-bottom:1.25rem;background:var(--ts-text)}
    .main-image{height:clamp(300px,38vw,430px);margin-bottom:1rem;box-shadow:inset 0 0 0 1px var(--ts-shadow-navy-750-a8-2)}
    .main-image img{display:block;object-position:center}
    .expand-image-btn{position:absolute;right:1rem;bottom:1rem;z-index:3;display:inline-flex;align-items:center;gap:.45rem;padding:.62rem .85rem;border:1px solid var(--ts-border-gray-0-a35);border-radius:9px;color:var(--ts-text-gray-0);background:var(--ts-bg-navy-900-a82-3);backdrop-filter:blur(8px);font-weight:700;cursor:pointer}
    .expand-image-btn:hover{background:var(--ts-bg-blue-500-5)}
    .category-list{display:grid;gap:.4rem;margin:0;padding:0;list-style:none}
    .category-list li{display:flex;align-items:flex-start;gap:.45rem;padding:.42rem .55rem;border:1px solid var(--ts-border-sky-100-12);border-radius:8px;background:var(--ts-text);color:var(--ts-text-navy-700-3);font-size:.78rem;line-height:1.3}
    .category-list i{color:var(--ts-text-blue-500-8);font-size:.9rem;flex:0 0 auto}
    .image-lightbox{position:fixed;inset:0;z-index:3000;display:grid;place-items:center;padding:2rem;background:var(--ts-bg-navy-950-a92);backdrop-filter:blur(8px)}
    .image-lightbox img{display:block;max-width:min(94vw,1500px);max-height:90vh;object-fit:contain;border-radius:12px;box-shadow:0 24px 80px var(--ts-shadow-gray-1000-a50)}
    .lightbox-close{position:fixed;top:1rem;right:1.25rem;z-index:3001;width:44px;height:44px;border:1px solid var(--ts-border-gray-0-a35);border-radius:50%;color:var(--ts-text-gray-0);background:var(--ts-bg-navy-850-a78);font-size:1.8rem;line-height:1;cursor:pointer}
    .info-grid{grid-template-columns:repeat(12,minmax(0,1fr))!important;align-items:start!important}.info-item{min-height:0!important;height:auto!important;background:var(--ts-bg-navy-850-39)!important;border-color:var(--ts-border-navy-700-12)!important;color:var(--ts-text)!important}.info-item label{color:var(--ts-text-sky-250-27)!important}.info-item:nth-child(1){grid-column:span 4!important}.info-item:nth-child(2){grid-column:span 8!important}.info-item:nth-child(3){grid-column:span 8!important}.info-item:nth-child(4),.info-item:nth-child(5){grid-column:span 2!important}.info-item:nth-child(6),.info-item:nth-child(7){grid-column:span 6!important}.category-list{grid-template-columns:repeat(2,minmax(0,1fr))}.category-list li{background:var(--ts-bg-navy-800-13)!important;border-color:var(--ts-border-navy-650-7)!important;color:var(--ts-text-sky-50-5)!important}.category-list i{color:var(--ts-text-blue-300-9)}.extras-section,.schedules-section,.actions-section,.info-section{background:var(--ts-bg-navy-850-40)!important;border-color:var(--ts-border-navy-700-13)!important}.section-header{background:var(--ts-bg-navy-800-2)!important;border-color:var(--ts-border-navy-700-14)!important}.section-header h2{color:var(--ts-text-on-accent-24)!important}.share-link-bar{border-color:var(--ts-border-navy-700-14)!important;background:var(--ts-bg-navy-800-2)!important}.share-link-info span{color:var(--ts-text-blue-150-7)!important}
    .featured-main-badge{position:absolute;top:1rem;left:1rem;display:inline-flex;align-items:center;gap:.42rem;padding:.55rem .72rem;border:1px solid var(--ts-border-gray-0-a78);border-radius:999px;color:var(--ts-text-amber-850);background:var(--ts-bg-amber-150-a94);box-shadow:0 7px 18px var(--ts-shadow-amber-850-a18);font-size:.76rem;font-weight:800;backdrop-filter:blur(8px)}
    .featured-main-badge span{color:var(--ts-text-amber-550);font-size:1rem}
    .image-gallery{gap:.75rem;padding:.35rem .15rem .5rem;scrollbar-width:thin}
    .gallery-thumb,.add-image-btn{width:112px;height:82px;border-radius:11px}
    .gallery-thumb{border:2px solid transparent;background:var(--ts-bg-sky-50-9)}.gallery-thumb.active{border-color:var(--ts-border-blue-500);box-shadow:0 0 0 3px var(--ts-shadow-blue-500-a13)}
    .feature-thumb-btn{position:absolute;left:5px;top:5px;display:grid;place-items:center;width:28px;height:28px;padding:0;border:1px solid var(--ts-border-gray-0-a90);border-radius:50%;color:var(--ts-text-gray-0);background:var(--ts-bg-navy-900-a88-2);font-size:.78rem;cursor:pointer;box-shadow:0 2px 6px var(--ts-shadow-gray-1000-a35)}
    .feature-thumb-btn:hover:not(:disabled),.feature-thumb-btn.featured{color:var(--ts-text-amber-800);background:var(--ts-bg-amber-300)}.feature-thumb-btn:disabled{cursor:default}
    .remove-thumb-btn{top:5px;right:5px;width:28px;height:28px;border:1px solid var(--ts-border-gray-0-a90)}
    .info-section,.extras-section,.schedules-section,.actions-section{padding:1.35rem;margin-bottom:1.1rem;background:var(--ts-text)}
    .section-header{gap:1rem;margin-bottom:1.1rem}.section-header h2{font-size:1.08rem}
    .share-link-bar{display:flex;align-items:center;justify-content:space-between;gap:1rem;margin-bottom:1.1rem;padding:.85rem 1rem;border:1px solid var(--ts-border-blue-100-3);border-radius:12px;background:var(--ts-bg-blue-50-14)}
    .share-link-info{display:flex;align-items:center;gap:.65rem;min-width:0}
    .share-link-info i{flex:0 0 auto;font-size:1.3rem;color:var(--ts-text-blue-500-9)}
    .share-link-info div{display:grid;gap:.1rem;min-width:0}
    .share-link-info small{color:var(--ts-text-sky-500-5);font-size:.66rem;font-weight:800;letter-spacing:.05em}
    .share-link-info span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:var(--ts-text-blue-650);font-size:.85rem;font-weight:600}
    .share-link-copy{flex:0 0 auto;display:inline-flex;align-items:center;gap:.4rem;padding:.55rem .9rem;border:0;border-radius:8px;color:var(--ts-on-primary);background:linear-gradient(135deg,var(--ts-bg-blue-500),var(--ts-bg-sky-450));box-shadow:0 6px 14px var(--ts-shadow-blue-500-a20-4);font-weight:700;font-size:.8rem;cursor:pointer}
    .share-link-copy:hover{filter:brightness(1.08)}
    @media(max-width:640px){.share-link-bar{flex-direction:column;align-items:stretch}.share-link-copy{justify-content:center}}
    .info-grid{grid-template-columns:repeat(12,minmax(0,1fr));grid-auto-flow:row;gap:.75rem;align-items:stretch}.info-item{min-width:0;min-height:0;height:auto;padding:.85rem;border:1px solid var(--ts-border-sky-50-4);background:linear-gradient(145deg,var(--ts-bg-sky-0-3),var(--ts-bg-sky-50-3))}
    .info-item:nth-child(1){grid-column:span 3}.info-item:nth-child(2){grid-column:span 5}.info-item:nth-child(3){grid-column:span 4}.info-item:nth-child(4){grid-column:span 6}.info-item:nth-child(5),.info-item:nth-child(6){grid-column:span 3}.info-item:nth-child(7),.info-item:nth-child(8){grid-column:span 6}.info-item label{color:var(--ts-text-blue-500-10)}.info-item span{overflow-wrap:anywhere;color:var(--ts-text-navy-750-5);font-size:.9rem;line-height:1.5}
    .extras-grid{grid-template-columns:repeat(auto-fill,minmax(260px,1fr))}.extra-card{border:1px solid var(--ts-border-sky-100-13);border-radius:12px;background:var(--ts-bg-sky-0-3)}.extra-card:hover{border-color:var(--ts-border-sky-200);background:var(--ts-bg-blue-0-5);transform:translateY(-2px)}
    .schedules-table-container{border:1px solid var(--ts-border-sky-100-11);border-radius:12px;overflow:auto}.schedules-table th{background:var(--ts-bg-sky-50-10)}
    .modal-overlay{padding:1.25rem;background:var(--ts-bg-navy-900-a66-3);backdrop-filter:blur(5px)}
    .modal-content{max-width:760px;border:1px solid var(--ts-border-strong);border-radius:20px;overflow:hidden;background:var(--ts-bg-elevated);box-shadow:0 30px 90px var(--ts-shadow-navy-900-a50)}
    .modal-content.modal-medium.modal-medium{width:min(760px,calc(100vw - 64px))!important;max-width:none!important}.modal-content.modal-wide.modal-wide{width:min(1080px,calc(100vw - 64px))!important;max-width:none!important}.modal-content.modal-large.modal-large{width:min(1180px,calc(100vw - 64px))!important;max-width:none!important}.modal-header{position:sticky;top:0;z-index:3;padding:1.1rem 1.35rem;border-bottom:1px solid var(--ts-border-strong);background:var(--ts-bg-navy-800)}
    .modal-header h2{color:var(--ts-text);font-size:1.08rem}.modal-header .close-btn{border:1px solid var(--ts-border-gray-0-a30);border-radius:50%;color:var(--ts-text)}.modal-header .close-btn:hover{color:var(--ts-text);background:var(--ts-bg-gray-0-a14)}
    .modal-body{max-height:calc(92vh - 78px);overflow-y:auto;padding:1.35rem;color:var(--ts-text);background:var(--ts-bg-elevated)}.modal-body .form-group{margin-bottom:1rem}.modal-body .form-control{box-sizing:border-box;border:1px solid var(--ts-divider);border-radius:9px;color:var(--ts-text-soft-2);background:var(--ts-scrollbar-track)}.modal-body .form-control:focus{border-color:var(--ts-border-blue-350-3);box-shadow:0 0 0 3px var(--ts-shadow-blue-350-a15)}
    .modal-wide .modal-body{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));grid-template-areas:"title title" "description description" "category category" "metrics metrics" "address address" "location location" "map map" "status status" "actions actions";gap:1rem}.modal-wide .modal-body>.form-group{margin:0}.modal-wide .modal-body>.form-group:nth-child(1){grid-area:title}.modal-wide .modal-body>.form-group:nth-child(2){grid-area:description}.modal-wide .modal-body>.form-group:nth-child(3){grid-area:category}.modal-wide .modal-body>.form-row:nth-child(4){grid-area:metrics}.modal-wide .modal-body>.form-group:nth-child(5){grid-area:address}.modal-wide .modal-body>.form-row:nth-child(6){grid-area:location}.modal-wide .modal-body>app-google-location-picker{grid-area:map}.modal-wide .modal-body>.status-field{grid-area:status}.modal-wide .modal-body>.modal-actions{grid-area:actions}
    .modal-medium .modal-body{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));grid-template-areas:"name price" "description description" "actions actions";gap:1rem}.modal-medium .modal-body>.form-group{margin:0}.modal-medium .modal-body>.form-group:nth-child(1){grid-area:name}.modal-medium .modal-body>.form-group:nth-child(2){grid-area:description}.modal-medium .modal-body>.form-group:nth-child(3){grid-area:price}.modal-medium .modal-body>.modal-actions{grid-area:actions}
    .modal-actions{position:sticky;bottom:-1.35rem;z-index:3;margin:1.25rem -1.35rem -1.35rem;padding:1rem 1.35rem;border-top:1px solid var(--ts-border-strong);background:var(--ts-bg-navy-800);box-shadow:0 -10px 22px var(--ts-shadow-gray-1000-a22)}
    .btn-primary,.btn-save{border-radius:9px;background:var(--ts-bg-blue-500-6)}.btn-secondary,.btn-cancel{border-radius:9px}.delete-image-btn{border-radius:9px;box-shadow:0 6px 15px var(--ts-shadow-red-650-a20)}
    /* Superficies y contraste definitivos del detalle. */
    .detalle-container{color:var(--ts-text);background:var(--ts-bg)}
    .detalle-header h1,.section-header h2{color:var(--ts-text-on-accent-3)}
    .back-btn{color:var(--ts-text-sky-200-7);background:var(--ts-bg-elevated);border-color:var(--ts-border-strong);box-shadow:none}
    .images-section,.info-section,.extras-section,.schedules-section,.actions-section{background:var(--ts-bg-elevated);border-color:var(--ts-border-strong);box-shadow:0 10px 24px var(--ts-shadow-gray-1000-a16)}
    .section-header{margin:-1.35rem -1.35rem 1.1rem;padding:1rem 1.35rem;background:var(--ts-bg-elevated)!important;border-color:var(--ts-border-strong)}
    .info-item{color:var(--ts-text);background:var(--ts-bg-navy-850-2);border-color:var(--ts-border-strong)}
    .info-item label{color:var(--ts-text-sky-350-3)}.info-item span{color:var(--ts-text-soft-2)}
    .pricing-tiers-summary{margin-top:1.1rem;padding-top:1.1rem;border-top:1px solid var(--ts-border-strong,rgba(148,173,207,.28))}
    .pricing-tiers-summary>label{display:block;margin-bottom:.6rem;color:var(--ts-text-sky-350-3);font-size:.72rem;font-weight:700;text-transform:uppercase;letter-spacing:.05em}
    .pricing-tiers-summary .tier-list{margin:0;display:flex;flex-wrap:wrap;gap:.5rem}
    .pricing-tiers-summary .tier-row{display:inline-flex;align-items:center;gap:.5rem;padding:.4rem .75rem;border-radius:999px;background:var(--ts-surface-2,#0f2743);border:1px solid var(--ts-border-strong,rgba(148,173,207,.28))}
    .pricing-tiers-summary .tier-range{font-size:.8rem;color:var(--ts-text-soft-2);font-weight:600}
    .pricing-tiers-summary .tier-price{font-size:.8rem;color:var(--ts-primary,#2f7cff);font-weight:700}
    .info-item .status-badge.active{color:var(--ts-text-green-300-9);background:var(--ts-bg-green-600-a16);border-color:var(--ts-border-green-450-a30)}
    .info-item .status-badge.unavailable{color:var(--ts-text-red-200-9);background:var(--ts-bg-red-450-a15);border-color:var(--ts-border-red-350-a30-2)}
    .btn-toggle-status{color:var(--ts-text-sky-150-20);background:var(--ts-scrollbar-track);border-color:var(--ts-divider)}
    .extra-card,.empty-extras,.empty-schedules{color:var(--ts-text-sky-100-9);background:var(--ts-bg-navy-850-2);border-color:var(--ts-border-strong)}
    .schedules-table-container{border-color:var(--ts-border-strong)}.schedules-table th{color:var(--ts-text-sky-200-16);background:var(--ts-bg-navy-800)}.schedules-table td{color:var(--ts-text-sky-50-2);border-color:var(--ts-border-strong)}
    .schedule-instructions,.schedule-instructions li,.schedule-instructions p{color:var(--ts-text-sky-200-3)}
    .modal-body .form-group>label,.modal-body .checkbox-label{color:var(--ts-text-sky-150-4)}
    .btn-cancel,.modal-actions .btn-secondary{color:var(--ts-text-sky-100);background:var(--ts-bg-elevated);border-color:var(--ts-border-navy-650)}
    @media(max-width:900px){.info-item:nth-child(n){grid-column:span 6}.info-item:nth-child(4),.info-item:nth-child(7),.info-item:nth-child(8){grid-column:1/-1}.modal-wide .modal-body,.modal-medium .modal-body{display:block}.modal-wide .modal-body>*+*,.modal-medium .modal-body>*+*{margin-top:1rem}}
    @media(max-width:780px){.detalle-content{padding:1rem}.section-header{align-items:flex-start;flex-direction:column}.schedule-actions{width:100%;display:grid;gap:.5rem}.schedule-actions button{width:100%;justify-content:center}.main-image{height:320px}.modal-content.modal-medium.modal-medium,.modal-content.modal-wide.modal-wide,.modal-content.modal-large.modal-large{width:calc(100vw - 24px)!important}}
    .modal-wide{width:min(980px,96vw)}
    /* El modal de edición pasó a usar pestañas (tab-nav + .tab-panel); la
       grilla "grid-template-areas" de arriba (título/descripción/.../mapa en
       fila) se diseñó para los 9 elementos que antes eran hijos directos de
       .modal-body en ese orden exacto -- ahora los hijos directos son
       tab-nav + 3 .tab-panel + modal-actions, así que ninguno cae en un área
       nombrada y la grilla vieja dejaba filas completas sin contenido
       (el hueco vacío reportado). Volvemos a flujo normal para esta modal. */
    .modal-wide .modal-body{display:block!important}
    .modal-wide .modal-body{align-items:start}
    .modal-wide .form-group,.modal-wide .form-row{width:100%;min-width:0}
    .modal-wide .form-row{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:1rem;align-items:start}
    .modal-wide .location-row{grid-template-columns:repeat(3,minmax(0,1fr))}
    .modal-wide .form-control{width:100%;min-height:48px;box-sizing:border-box}
    .modal-wide textarea.form-control{min-height:112px;resize:vertical}
    .modal-wide .category-field{grid-column:1/-1}
    @media(max-width:760px){.modal-wide .form-row,.modal-wide .location-row{grid-template-columns:1fr}}

    /* Pestañas dentro del modal de edición */
    .tab-nav{display:flex;gap:.35rem;margin-bottom:1.3rem;padding-bottom:.1rem;border-bottom:1px solid var(--ts-border-strong,rgba(148,173,207,.28));overflow-x:auto}
    .tab-nav button{display:inline-flex;align-items:center;gap:.4rem;padding:.6rem .95rem;border:none;background:transparent;color:var(--ts-text-muted,#91a4bf);font-weight:600;font-size:.85rem;border-radius:9px 9px 0 0;cursor:pointer;white-space:nowrap;transition:color .2s ease,background .2s ease}
    .tab-nav button i{font-size:.95rem}
    .tab-nav button:hover{color:var(--ts-text,#f4f8ff);background:var(--ts-bg-gray-0-a5-2)}
    .tab-nav button.active{color:var(--ts-text,#f4f8ff);background:var(--ts-bg-blue-400-a14);box-shadow:inset 0 -2px 0 var(--ts-primary,#2f7cff)}
    @media(max-width:600px){.tab-nav button{padding:.5rem .65rem;font-size:.76rem;gap:.3rem}.tab-nav button i{font-size:.85rem}}
    .tab-panel{animation:tabFadeIn .18s ease}
    .tab-panel-hidden{display:none!important}
    @keyframes tabFadeIn{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:translateY(0)}}
    .tab-hint{display:flex;align-items:flex-start;gap:.5rem;margin:.9rem 0;padding:.65rem .85rem;border-radius:10px;background:var(--ts-bg-blue-400-a8);color:var(--ts-text-muted,#b7c6df)!important;font-size:.78rem;line-height:1.55}
    .tab-hint i{flex:0 0 auto;margin-top:.15rem;color:var(--ts-primary,#2f7cff)}
    .tab-hint strong{color:var(--ts-text,#f4f8ff)}

    /* Tarifas escalonadas */
    .pricing-tiers-block{margin-top:.9rem;padding-top:1rem;border-top:1px dashed var(--ts-border-strong,rgba(148,173,207,.28))}
    .metrics-row{align-items:start}
    .pricing-tiers-block h3{margin:0 0 .3rem;font-size:1rem;color:var(--ts-text,#f4f8ff)}
    .tier-optional{font-size:.72rem;font-weight:500;color:var(--ts-text-muted,#91a4bf)}
    .tiers-loading,.empty-tiers{padding:1rem;text-align:center;color:var(--ts-text-muted,#91a4bf);font-size:.85rem;border:1px dashed var(--ts-border-strong,rgba(148,173,207,.28));border-radius:10px}
    .tier-list{list-style:none;margin:0 0 1rem;padding:0;display:flex;flex-direction:column;gap:.5rem}
    .tier-row{display:flex;align-items:center;gap:.75rem;padding:.6rem .85rem;border:1px solid var(--ts-border-strong,rgba(148,173,207,.28));border-radius:10px;background:var(--ts-bg-elevated,#0a1c34)}
    .tier-range{flex:1;font-weight:600;color:var(--ts-text,#f4f8ff);font-size:.88rem}
    .tier-price{font-weight:700;color:var(--ts-primary,#2f7cff)}
    .tier-delete-btn{display:flex;align-items:center;justify-content:center;width:30px;height:30px;border:none;border-radius:8px;background:transparent;color:var(--ts-text-muted,#91a4bf);cursor:pointer;transition:background .2s ease,color .2s ease}
    .tier-delete-btn:hover:not(:disabled){background:var(--ts-bg-red-350-a14);color:var(--ts-danger,#f05259)}
    .tier-delete-btn:disabled{opacity:.6;cursor:not-allowed}
    .tier-add-form{display:grid;grid-template-columns:repeat(3,minmax(0,1fr)) auto;gap:.7rem;align-items:start}
    .tier-add-form .form-group{margin:0;position:relative;padding-bottom:34px}
    .tier-add-form .form-group .error-text{position:absolute;left:0;right:0;bottom:0;margin:2px 0 0;line-height:1.3}
    .tier-add-btn-wrap label{visibility:hidden}
    .tier-add-btn{white-space:nowrap;height:fit-content}
    @media(max-width:640px){.tier-add-form{grid-template-columns:1fr 1fr;}.tier-add-btn-wrap{grid-column:1/-1}.tier-add-btn-wrap label{display:none}.tier-add-btn{width:100%;justify-content:center}}
    @media(max-width:520px){.detalle-header h1{font-size:1rem}.info-grid{grid-template-columns:1fr}.info-item:nth-child(n){grid-column:1/-1}.main-image{height:280px}.gallery-thumb,.add-image-btn{width:92px;height:72px}.modal-overlay{padding:.5rem}.modal-content{max-height:96vh;border-radius:14px}}

    /* Distribución estable y responsive de la información del espacio. */
    .info-section .info-grid{
      display:grid;
      grid-template-columns:repeat(12,minmax(0,1fr))!important;
      gap:.75rem;
      align-items:stretch!important;
    }
    .info-section .info-item{
      display:flex;
      min-width:0;
      flex-direction:column;
      justify-content:flex-start;
      gap:.4rem;
      padding:.9rem!important;
      border-radius:11px;
    }
    .info-section .info-item label{margin:0;font-size:.7rem;letter-spacing:.055em;text-transform:uppercase}
    .info-section .info-item span{font-size:.84rem;line-height:1.42;overflow-wrap:anywhere}
    .info-section .info-title{grid-column:span 5!important}
    .info-section .info-categories{grid-column:span 7!important;grid-row:span 2}
    .info-section .info-description{grid-column:span 5!important}
    .info-section .info-capacity,.info-section .info-price{grid-column:span 2!important}
    .info-section .info-address{grid-column:span 4!important}
    .info-section .info-location{grid-column:span 4!important}
    .info-section .category-list{
      grid-template-columns:repeat(2,minmax(0,1fr));
      gap:.36rem;
    }
    .info-section .category-list li{min-width:0;padding:.38rem .5rem;font-size:.7rem;overflow-wrap:anywhere}
    .admin-space-map{margin-top:1rem;overflow:hidden;border:1px solid var(--ts-border-navy-650-8);border-radius:15px;background:linear-gradient(145deg,var(--ts-bg-navy-850-14),var(--ts-bg-navy-900-8));box-shadow:0 14px 32px var(--ts-shadow-gray-1000-a20)}.admin-space-map header{display:flex;align-items:center;justify-content:space-between;gap:1rem;padding:15px 17px}.admin-space-map header>div{min-width:0}.admin-space-map small{color:var(--ts-text-blue-250-21);font-size:.62rem;font-weight:850;letter-spacing:.11em}.admin-space-map h3{margin:4px 0;color:var(--ts-text);font-size:1rem}.admin-space-map p{margin:0;color:var(--ts-text-sky-300-22);font-size:.72rem;line-height:1.45}.admin-space-map a{display:inline-flex;align-items:center;justify-content:center;gap:7px;flex:none;padding:10px 13px;border:1px solid var(--ts-border-blue-300-a52);border-radius:9px;color:var(--ts-on-primary);background:linear-gradient(135deg,var(--ts-primary),var(--ts-bg-indigo-350-6));text-decoration:none;font-size:.7rem;font-weight:800}.admin-space-map a:hover{filter:brightness(1.12)}.admin-space-map iframe{display:block;width:100%;height:330px;border:0;border-top:1px solid var(--ts-border-navy-650-8)}

    @media (max-width:1024px){
      .detalle-content{width:100%;max-width:none;box-sizing:border-box}
      .info-section .info-title,.info-section .info-description{grid-column:span 5!important}
      .info-section .info-categories{grid-column:span 7!important}
      .info-section .info-capacity,.info-section .info-price{grid-column:span 3!important}
      .info-section .info-address,.info-section .info-location{grid-column:span 6!important}
      .extras-grid{grid-template-columns:repeat(2,minmax(0,1fr))}
    }
    @media (max-width:760px){
      .info-section,.extras-section,.schedules-section,.actions-section{padding:1rem}
      .section-header{margin:-1rem -1rem 1rem;padding:.9rem 1rem}
      .info-section .info-title,.info-section .info-description{grid-column:span 7!important}
      .info-section .info-capacity,.info-section .info-price{grid-column:span 5!important}
      .info-section .info-categories{grid-column:1/-1!important;grid-row:auto}
      .info-section .info-address,.info-section .info-location{grid-column:span 6!important}
      .extras-grid{grid-template-columns:repeat(2,minmax(0,1fr))}
      .extra-card{min-width:0}
      .admin-space-map header{align-items:flex-start;flex-direction:column}.admin-space-map a{width:100%;box-sizing:border-box}.admin-space-map iframe{height:280px}
    }
    @media (max-width:520px){
      .detalle-content{padding:.75rem}
      .info-section .info-grid{grid-template-columns:repeat(2,minmax(0,1fr))!important;gap:.6rem}
      .info-section .info-item{min-width:0;padding:.78rem!important}
      .info-section .info-title,
      .info-section .info-categories,
      .info-section .info-description,
      .info-section .info-address,
      .info-section .info-location{grid-column:1/-1!important}
      .info-section .info-capacity,
      .info-section .info-price{grid-column:span 1!important}
      .info-section .category-list{grid-template-columns:repeat(2,minmax(0,1fr))}
      .info-section .category-list li{align-items:center;padding:.36rem .42rem;font-size:.64rem;line-height:1.25}
      .extras-grid{grid-template-columns:1fr}
      .section-header{gap:.7rem}
      .section-header .btn-primary,.section-header .btn-secondary{max-width:100%;justify-content:center}
    }
    @media (min-width:400px) and (max-width:520px){
      .section-header{display:grid;grid-template-columns:minmax(0,1fr) auto;align-items:center}
      .section-header .btn-primary,.section-header .btn-secondary{width:auto;white-space:nowrap}
    }
    @media (max-width:380px){
      .info-section .category-list{grid-template-columns:1fr}
      .section-header .btn-primary,.section-header .btn-secondary{width:100%}
    }

    /* La tabla se transforma en tarjetas legibles sin desplazamiento lateral. */
    @media (max-width:900px){
      .schedules-table-container{overflow:visible;border:0;background:transparent}
      .schedules-table{display:block;width:100%;min-width:0;border:0}
      .schedules-table thead{display:none}
      .schedules-table tbody{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.65rem}
      .schedules-table tr{
        display:grid;
        grid-template-columns:repeat(2,minmax(0,1fr));
        gap:.55rem .65rem;
        min-width:0;
        padding:.75rem;
        border:1px solid var(--ts-border-strong);
        border-radius:11px;
        background:var(--ts-bg-navy-850-2);
      }
      .schedules-table tr:hover{background:var(--ts-bg-navy-850-39)}
      .schedules-table td{
        display:flex;
        min-width:0;
        flex-direction:column;
        gap:.22rem;
        padding:0;
        border:0;
        font-size:.72rem;
        overflow-wrap:anywhere;
      }
      .schedules-table td::before{
        content:attr(data-label);
        color:var(--ts-text-sky-350-29);
        font-size:.56rem;
        font-weight:800;
        letter-spacing:.055em;
        text-transform:uppercase;
      }
      .schedules-table .schedule-day,.schedules-table .schedule-status{justify-content:space-between}
      .schedules-table .actions-cell{grid-column:1/-1;display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.45rem}
      .schedules-table .actions-cell::before{grid-column:1/-1}
      .schedules-table .btn-action{width:100%;min-height:32px;padding:.38rem .5rem}
    }
    @media (max-width:600px){
      .schedules-table tbody{grid-template-columns:1fr}
      .schedules-section .section-header{display:block}
      .schedules-section .section-header h2{margin:0 0 .7rem;font-size:.94rem;line-height:1.2}
      .schedules-section .schedule-actions{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.45rem}
      .schedules-section .schedule-actions button{min-width:0;min-height:34px;padding:.4rem .45rem;font-size:.62rem;white-space:normal;line-height:1.2}
      .schedules-table tr{
        grid-template-columns:repeat(12,minmax(0,1fr));
        gap:.55rem .45rem;
        padding:.7rem .75rem;
      }
      .schedules-table .schedule-day{grid-column:span 7}
      .schedules-table .schedule-status{grid-column:span 5;align-items:flex-end}
      .schedules-table .schedule-opening{grid-column:span 3}
      .schedules-table .schedule-closing{grid-column:span 3}
      .schedules-table .actions-cell{grid-column:span 6;align-self:end;display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.35rem}
      .schedules-table .actions-cell::before{display:none}
      .schedules-table .status-open,.schedules-table .status-closed{width:auto;max-width:100%;padding:.25rem .55rem;font-size:.6rem;line-height:1.1;text-align:center}
      .schedules-table .btn-action{min-height:28px;padding:.25rem .32rem;border-radius:6px;font-size:.58rem;line-height:1.1}
      .schedules-table td::before{font-size:.5rem}
    }
    @media (min-width:601px) and (max-width:780px){
      .schedule-actions{grid-template-columns:repeat(2,minmax(0,1fr))}
    }
    @media (max-width:360px){
      .schedules-table .schedule-day,.schedules-table .schedule-status{grid-column:span 6}
      .schedules-table .schedule-opening,.schedules-table .schedule-closing{grid-column:span 3}
      .schedules-table .actions-cell{grid-column:span 6}
    }

    .schedule-delete-overlay{padding:1rem;background:var(--ts-bg-navy-950-a82-4);backdrop-filter:blur(8px)}
    .schedule-delete-modal{position:relative;width:min(480px,100%);padding:1.35rem;color:var(--ts-text-on-accent-25);border:1px solid var(--ts-border-red-300-a38-2);border-radius:18px;background:radial-gradient(circle at 100% 0,var(--ts-bg-red-450-a15-2),transparent 38%),var(--ts-bg-elevated);box-shadow:0 28px 80px var(--ts-shadow-gray-1000-a52)}
    .schedule-delete-close{position:absolute;top:.75rem;right:.75rem;display:grid;place-items:center;width:32px;height:32px;color:var(--ts-text-sky-200-17);border:1px solid var(--ts-border-sky-300-a25);border-radius:50%;background:var(--ts-bg-navy-900-17);font-size:1.15rem;cursor:pointer}
    .schedule-delete-icon{display:grid;place-items:center;width:52px;height:52px;margin-bottom:.7rem;color:var(--ts-text-red-200-10);border:1px solid var(--ts-border-red-300-a28);border-radius:15px;background:var(--ts-bg-red-450-a10);font-size:1.3rem}
    .schedule-delete-label{display:inline-flex;padding:.22rem .45rem;color:var(--ts-text-red-200-11);border:1px solid var(--ts-border-red-300-a23);border-radius:999px;background:var(--ts-bg-red-450-a8-3);font-size:.56rem;font-weight:850;letter-spacing:.07em;text-transform:uppercase}
    .schedule-delete-modal h2{margin:.6rem 2rem .35rem 0;color:var(--ts-text);font-size:1.18rem}.schedule-delete-modal>p{margin:0;color:var(--ts-text-soft);font-size:.72rem;line-height:1.5}
    .schedule-delete-summary{display:grid;grid-template-columns:1fr auto 1fr;gap:.55rem;align-items:center;margin:.9rem 0;padding:.75rem;border:1px solid var(--ts-border-strong);border-radius:11px;background:var(--ts-bg-navy-850-14)}.schedule-delete-summary>span{display:grid;gap:.15rem}.schedule-delete-summary small{color:var(--ts-text-sky-350-30);font-size:.56rem;text-transform:uppercase}.schedule-delete-summary strong{font-size:.8rem}.schedule-delete-summary>i{color:var(--ts-text-sky-450-13)}.schedule-delete-state{grid-column:1/-1;padding-top:.55rem;border-top:1px solid var(--ts-border-sky-350-a18)}
    .schedule-delete-warning{display:flex;gap:.55rem;align-items:flex-start;padding:.65rem;color:var(--ts-text-red-150-12);border-radius:10px;background:var(--ts-bg-red-450-a8-4);font-size:.64rem;line-height:1.45}.schedule-delete-warning i{color:var(--ts-text-red-250-9)}
    .schedule-delete-actions{display:grid;grid-template-columns:1fr 1fr;gap:.55rem;margin-top:1rem}.schedule-delete-actions button{min-height:39px;padding:.55rem .65rem;border-radius:9px;font-size:.68rem;font-weight:800;cursor:pointer}.btn-confirm-delete{display:flex;align-items:center;justify-content:center;gap:.4rem;color:var(--ts-on-primary);border:1px solid var(--ts-border-red-400-2);background:linear-gradient(135deg,var(--ts-bg-red-450),var(--ts-bg-red-550-2))}.btn-confirm-delete:disabled{cursor:wait;opacity:.65}

    @media (max-width:600px){
      .schedules-table tr{gap:.35rem .4rem;padding:.52rem .6rem}
      .schedules-table .schedule-day,.schedules-table .schedule-opening,.schedules-table .schedule-closing,.schedules-table .schedule-status{grid-column:span 3}
      .schedules-table .schedule-status{align-items:flex-start}
      .schedules-table td{gap:.12rem;font-size:.62rem;line-height:1.15}
      .schedules-table td::before{font-size:.43rem;line-height:1}
      .schedules-table .actions-cell{grid-column:1/-1;gap:.35rem;margin-top:.12rem}
      .schedules-table .btn-action{min-height:25px;padding:.2rem .3rem;font-size:.54rem}
      .schedules-table .status-open,.schedules-table .status-closed{padding:.2rem .4rem;font-size:.52rem}
      .schedule-delete-modal{padding:1.1rem}.schedule-delete-actions{grid-template-columns:1fr}.schedule-delete-actions button{width:100%}
    }
    @media (max-width:360px){
      .schedules-table .schedule-day,.schedules-table .schedule-opening,.schedules-table .schedule-closing,.schedules-table .schedule-status{grid-column:span 6}
    }
    .schedule-edit-modal.schedule-edit-modal{box-sizing:border-box;width:min(680px,calc(100vw - 32px))!important;max-width:680px!important;overflow:hidden!important}
    .schedule-edit-modal .modal-body{box-sizing:border-box;width:100%;overflow-y:auto;overflow-x:hidden}
    .schedule-edit-modal .modal-body>.form-row{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.8rem;width:100%;min-width:0}
    .schedule-edit-modal .form-group{min-width:0}.schedule-edit-modal .form-control{width:100%;min-width:0}
    .schedule-edit-modal .modal-actions{box-sizing:border-box;width:auto;max-width:none;overflow:hidden}
    .schedule-edit-modal label i{color:var(--ts-text-sky-350-3)!important}
    @media(max-width:520px){
      .schedule-edit-modal.schedule-edit-modal{width:calc(100vw - 24px)!important}
      .schedule-edit-modal .modal-body>.form-row{grid-template-columns:repeat(2,minmax(0,1fr));gap:.55rem}
      .schedule-edit-modal .modal-header,.schedule-edit-modal .modal-body{padding-inline:.85rem!important}
      .schedule-edit-modal .modal-actions{margin-inline:-.85rem;padding-inline:.85rem!important}
    }
    @media(max-width:340px){.schedule-edit-modal .modal-body>.form-row{grid-template-columns:1fr}}



    /* Modal de horarios: un renglón por día. */
    .sched-modal { max-width: 760px; }
    .sched-body { display: grid; gap: .8rem; }
    .sched-intro { display: flex; gap: .5rem; margin: 0; padding: .65rem .8rem; border-radius: 10px; color: var(--ts-text-soft); background: var(--ts-primary-soft-14, rgba(47,124,255,.1)); font-size: .82rem; line-height: 1.45; }
    .sched-intro i { color: var(--ts-primary); margin-top: .1rem; }
    .sched-presets { display: flex; flex-wrap: wrap; align-items: center; gap: .4rem; color: var(--ts-text-muted); font-size: .78rem; }
    .sched-chip { height: 32px; min-height: 32px !important; padding: 0 .7rem !important; border: 1px solid var(--ts-border-strong); border-radius: 999px; color: var(--ts-text-soft); background: var(--ts-surface); font: inherit; font-size: .76rem !important; font-weight: 650; cursor: pointer; }
    .sched-chip:hover { border-color: var(--ts-primary); color: var(--ts-text); }
    .sched-days { display: grid; gap: .4rem; }
    .sched-day { display: flex; flex-wrap: wrap; align-items: center; gap: .4rem .9rem; min-height: 52px; padding: .35rem .75rem; border: 1px solid var(--ts-border); border-radius: 11px; background: var(--ts-surface); }
    .sched-day.closed { opacity: var(--ts-closed-row-opacity, .72); background: var(--ts-closed-row-bg, var(--ts-surface)); }
    .sched-day.invalid { border-color: var(--ts-danger, #f05259); }
    .sched-switch { display: inline-flex; align-items: center; gap: .55rem; min-width: 130px; margin: 0; color: var(--ts-text); font-size: .88rem; font-weight: 700; cursor: pointer; }
    .sched-switch input { width: 17px; height: 17px; min-height: 0 !important; margin: 0; accent-color: var(--ts-primary, #2f7cff); }
    .sched-times { display: flex; align-items: center; gap: .45rem; margin-left: auto; color: var(--ts-text-muted); font-size: .8rem; }
    .sched-modal .sched-times input, .sched-modal .sched-times ::ng-deep input { width: 124px !important; height: 38px !important; min-height: 38px !important; padding: .2rem .65rem !important; border-radius: 10px !important; font-size: .86rem !important; box-sizing: border-box; }
    .sched-closed-label { margin-left: auto; color: var(--ts-text-muted); font-size: .8rem; }
    .sched-err { flex-basis: 100%; color: var(--ts-danger-text, #ff8a8a); font-size: .76rem; font-weight: 650; }
    .sched-err-block { margin: 0; }
    @media (max-width: 560px) {
      .sched-times { margin-left: 0; width: 100%; }
      .sched-modal .sched-times input, .sched-modal .sched-times ::ng-deep input { width: 100% !important; }
      .sched-times > span { flex: none; }
    }

  `]
})
export class SalaDetalleComponent implements OnInit {
  readonly categories = [
    { value: 'Coworking', label: 'Espacio de coworking' }, { value: 'Sala de reuniones', label: 'Sala de reuniones' },
    { value: 'Oficina privada', label: 'Oficina privada' }, { value: 'Sala de capacitación', label: 'Sala para capacitaciones' },
    { value: 'Sala de entrevistas', label: 'Sala para entrevistas o consultas' }, { value: 'Sala de juntas', label: 'Sala de juntas directivas' },
    { value: 'Eventos', label: 'Salón para eventos y celebraciones' }, { value: 'Conferencias', label: 'Auditorio o sala de conferencias' },
    { value: 'Estudio creativo', label: 'Taller o estudio creativo' }, { value: 'Podcast', label: 'Estudio para podcast y grabación de audio' },
    { value: 'Fotografía', label: 'Estudio fotográfico' }, { value: 'Video', label: 'Estudio de video y producción audiovisual' },
    { value: 'Consultorio privado', label: 'Consultorio privado' }, { value: 'Aula o salón de clases', label: 'Aula o salón de clases' },
    { value: 'Taller práctico', label: 'Espacio para talleres prácticos' }, { value: 'Estudio de música y ensayo', label: 'Estudio de música y sala de ensayo' },
    { value: 'Cocina para eventos o producción', label: 'Cocina para eventos o producción' }, { value: 'Bienestar y yoga', label: 'Espacio para bienestar, yoga o terapias' },
    { value: 'Terraza o espacio al aire libre', label: 'Terraza o espacio al aire libre' }, { value: 'Sala de exhibición', label: 'Sala de exhibición o showroom' },
    { value: 'Videojuegos y esports', label: 'Sala de videojuegos y esports' }, { value: 'Cabina para videollamadas', label: 'Cabina privada para videollamadas' },
    { value: 'Espacio infantil', label: 'Espacio para actividades infantiles' }, { value: 'Otro', label: 'Otro tipo de espacio' },
  ];
  readonly departments: string[] = [...EL_SALVADOR_DEPARTMENTS];
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('editFormRef') editFormRef!: ElementRef<HTMLFormElement>;
  @ViewChild('editScheduleFormRef') editScheduleFormRef!: ElementRef<HTMLFormElement>;
  
  espacio = signal<Espacio | null>(null);
  currentImageIndex = signal<number>(0);
  expandedImage = signal<string | null>(null);
  showEditModal = signal<boolean>(false);
  activeEditTab = signal<'general' | 'precio' | 'ubicacion'>('general');
  private readonly EDIT_TAB_BY_FIELD: Record<string, 'general' | 'precio' | 'ubicacion'> = {
    title: 'general', description: 'general', categories: 'general', is_active: 'general',
    capacity: 'precio', price_per_hour: 'precio',
    country: 'ubicacion', department: 'ubicacion', city: 'ubicacion', address_line1: 'ubicacion', latitude: 'ubicacion', longitude: 'ubicacion',
  };
  pricingTiers = signal<SpacePricingTier[]>([]);
  loadingTiers = signal<boolean>(false);
  addingTier = signal<boolean>(false);
  deletingTier = signal<number | null>(null);
  tierForm!: FormGroup;
  showScheduleModal = signal<boolean>(false);
  /** Se intentó guardar el modal de horarios (desde ahí se muestran errores). */
  readonly scheduleSubmitted = signal(false);
  /** Recrea los selectores de hora solo con cambios externos (atajos). */
  private scheduleGen = 0;
  editingSchedule = signal<SpaceSchedule | null>(null);
  schedulePendingDeletion = signal<SpaceSchedule | null>(null);
  deletingSchedule = signal<boolean>(false);
  isSavingSchedules = signal<boolean>(false);
  isSavingSchedule = signal<boolean>(false);
  deletingImage = signal<number | null>(null);
  uploadingImages = signal<boolean>(false);
  readonly imageAccept = IMAGE_UPLOAD_ACCEPT;
  isSavingFeatured = signal<boolean>(false);
  editForm!: FormGroup;
  scheduleForm!: FormGroup;
  editScheduleForm!: FormGroup;
  daysOfWeek = [
    { name: 'Domingo', value: 0 },
    { name: 'Lunes', value: 1 },
    { name: 'Martes', value: 2 },
    { name: 'Miércoles', value: 3 },
    { name: 'Jueves', value: 4 },
    { name: 'Viernes', value: 5 },
    { name: 'Sábado', value: 6 }
  ];
  private readonly IMAGES_BASE_URL = 'https://storage.googleapis.com/thehubp/spaces/';

  categoryLabels(): string {
    const space = this.espacio();
    const selected = space?.categories?.length ? space.categories : [space?.category || 'Sin categoría asignada'];
    return selected.map(value => this.categories.find(option => option.value === value)?.label || value).join(' · ');
  }

  setEditCategories(values: string[]): void {
    const control = this.editForm.get('categories');
    control?.setValue(values);
    control?.markAsTouched();
    control?.markAsDirty();
  }

  setEditCity(value: string): void {
    const control = this.editForm.get('city');
    control?.setValue(value);
    control?.markAsTouched();
    control?.markAsDirty();
  }

  setEditDepartment(value: string): void {
    const control = this.editForm.get('department');
    control?.setValue(value);
    control?.markAsTouched();
    control?.markAsDirty();
    const cityControl = this.editForm.get('city');
    const currentCity = cityControl?.value;
    if (currentCity && !citiesForDepartment(value).includes(currentCity)) {
      cityControl?.setValue('');
    }
  }

  citiesForSelectedEditDepartment(): string[] {
    return citiesForDepartment(this.editForm?.get('department')?.value);
  }

  currentImage(): EspacioImage | null {
    if (!this.espacio() || !this.espacio()!.images || this.espacio()!.images.length === 0) {
      return null;
    }
    return this.espacio()!.images[this.currentImageIndex()];
  }

  expandCurrentImage(): void {
    const imageUrl = this.imagenActual();
    if (imageUrl) this.expandedImage.set(this.getImageUrl(imageUrl));
  }

  categoryLabelList(): string[] {
    const space = this.espacio();
    if (!space) return [];
    const categories = Array.isArray(space.categories) && space.categories.length
      ? space.categories
      : (space.category ? [space.category] : []);
    return categories.map(category => this.categories.find(option => option.value === category)?.label || category);
  }

  visibleSchedules(): SpaceSchedule[] {
    return this.spaceScheduleService.schedules().filter(schedule => !schedule.is_closed);
  }

  readonly mapEmbedUrl = signal<SafeResourceUrl | null>(null);
  readonly mapDirectionsUrl = signal('');

  private updateReadOnlyMap(space: Espacio | null): void {
    const latitude = Number(space?.latitude);
    const longitude = Number(space?.longitude);
    if (!space || space.latitude == null || space.longitude == null || !Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      this.mapEmbedUrl.set(null);
      this.mapDirectionsUrl.set('');
      return;
    }
    const destination = `${latitude},${longitude}`;
    this.mapDirectionsUrl.set(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`);
    this.mapEmbedUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(
      `https://www.google.com/maps?q=${encodeURIComponent(destination)}&z=17&output=embed&hl=es`,
    ));
  }

  commissionRate = signal<number>(5);

  netPricePerHour(): number {
    const price = Number(this.editForm?.get('price_per_hour')?.value) || 0;
    if (price <= 0) return 0;
    return price * (1 - this.commissionRate() / 100);
  }

  /** Crear/editar/quitar días no disponibles exige spaces.edit. */
  readonly canEditBlocks = inject(AuthService).hasPermission('spaces', 'edit');

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private salaService: SalaService,
    public spaceScheduleService: SpaceScheduleService,
    private fb: FormBuilder,
    private dialogs: DialogService,
    private sanitizer: DomSanitizer,
    private platformSettings: PlatformSettingsService,
    private notifications: NotificationService,
    private cooldown: ActionCooldownService,
  ) {
    this.initForms();
    effect(() => this.updateReadOnlyMap(this.espacio()));
    this.platformSettings.get().subscribe({
      next: (r) => this.commissionRate.set(r.settings.commission_rate),
      error: () => {},
    });
  }

  initForms(): void {
    this.editForm = this.fb.group({
      title: ['', Validators.required],
      description: ['', Validators.required],
      categories: [['Otro'], [Validators.required, Validators.maxLength(8)]],
      capacity: [0, [Validators.required, Validators.min(1)]],
      price_per_hour: [0, [Validators.required, Validators.min(0)]],
      address_line1: ['', Validators.required],
      city: ['', Validators.required],
      department: ['', Validators.required],
      country: ['', Validators.required],
      latitude: [null, [Validators.min(-90), Validators.max(90)]],
      longitude: [null, [Validators.min(-180), Validators.max(180)]],
      google_place_id: [null],
      is_active: [true]
    });

    this.tierForm = this.fb.group({
      min_hours: [null, [Validators.required, Validators.min(1), halfHourIncrementValidator]],
      max_hours: [null, [Validators.required, Validators.min(1), halfHourIncrementValidator]],
      total_price: [null, [Validators.required, Validators.min(0)]],
    });
  }

  initScheduleForm(): void {
    const formControls: any = {};
    this.daysOfWeek.forEach((day, index) => {
      formControls[`day_${index}_opening`] = ['08:00'];
      formControls[`day_${index}_closing`] = ['17:00'];
      formControls[`day_${index}_closed`] = [false];
    });
    this.scheduleForm = this.fb.group(formControls);
  }

  initEditScheduleForm(schedule: SpaceSchedule): void {
    this.editScheduleForm = this.fb.group({
      opening_time: [this.spaceScheduleService.formatTime(schedule.opening_time)],
      closing_time: [this.spaceScheduleService.formatTime(schedule.closing_time)],
      is_closed: [schedule.is_closed]
    });
  }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      const espacioId = parseInt(id);
      // El listado contiene una proyección resumida. El detalle siempre consulta
      // el contrato completo para no perder dirección, departamento u otros campos.
      this.salaService.getEspacioById(espacioId).subscribe({
        next: (espacio) => {
          this.espacio.set(espacio);
          this.salaService.espacios.update(items => items.map(item =>
            item.space_id === espacio.space_id ? { ...item, ...espacio } : item
          ));
          this.spaceScheduleService.loadSchedules(espacioId);
          // Antes las tarifas escalonadas solo se cargaban al abrir el modal
          // de edición -- la vista de información nunca las mostraba, así
          // que había que entrar a editar solo para verlas.
          this.loadPricingTiers(espacioId);
        },
        error: (error) => {
          console.error('Error al cargar espacio:', error);
          this.notifications.error('Error al cargar el espacio');
          setTimeout(() => this.navigateToSpaces(), 1000);
        }
      });
    }
    this.initScheduleForm();
  }

  // Modal Edit Info
  openEditModal(): void {
    const espacio = this.espacio();
    if (espacio) {
      this.editForm.patchValue({
        title: espacio.title,
        description: espacio.description,
        categories: espacio.categories?.length ? espacio.categories : [espacio.category || 'Otro'],
        capacity: espacio.capacity,
        price_per_hour: parseFloat(espacio.price_per_hour),
        address_line1: espacio.address_line1,
        city: espacio.city,
        department: espacio.department,
        country: espacio.country,
        latitude: espacio.latitude ?? null,
        longitude: espacio.longitude ?? null,
        google_place_id: espacio.google_place_id ?? null,
        is_active: espacio.is_active
      });
      this.activeEditTab.set('general');
      this.showEditModal.set(true);
      this.loadPricingTiers(espacio.space_id);
    }
  }

  closeEditModal(): void {
    this.showEditModal.set(false);
  }

  loadPricingTiers(spaceId: number): void {
    this.loadingTiers.set(true);
    this.salaService.getPricingTiers(spaceId).subscribe({
      next: (response) => {
        this.pricingTiers.set(response.pricing_tiers);
        this.loadingTiers.set(false);
      },
      error: () => {
        this.pricingTiers.set([]);
        this.loadingTiers.set(false);
      },
    });
  }

  isDeletingTier(tier: SpacePricingTier): boolean {
    return this.deletingTier() === tier.tier_id;
  }

  addPricingTier(): void {
    if (this.tierForm.invalid) {
      this.tierForm.markAllAsTouched();
      this.notifications.error('Completa el rango de horas y el precio antes de agregar la tarifa.');
      return;
    }
    const espacio = this.espacio();
    if (!espacio) return;
    const { min_hours, max_hours, total_price } = this.tierForm.value;

    this.addingTier.set(true);
    this.salaService.crearPricingTier(espacio.space_id, { min_hours, max_hours, total_price }).subscribe({
      next: (nuevoTier) => {
        this.pricingTiers.update(list => [...list, nuevoTier].sort((a, b) => a.min_hours - b.min_hours));
        this.tierForm.reset();
        this.addingTier.set(false);
        this.notifications.success('Tarifa escalonada agregada exitosamente.');
      },
      error: (error) => {
        this.addingTier.set(false);
        this.notifications.error(friendlyError(error, 'No fue posible agregar la tarifa.'));
      },
    });
  }

  async deletePricingTier(tier: SpacePricingTier): Promise<void> {
    this.deletingTier.set(tier.tier_id);
    try {
      const ok = await this.dialogs.confirmAndRun(
        { title: 'Eliminar tarifa escalonada', message: `La tarifa de ${tier.min_hours}-${tier.max_hours} horas dejará de aplicarse.`, tone: 'danger', confirmText: 'Eliminar' },
        () => this.salaService.eliminarPricingTier(tier.tier_id),
        'Tarifa eliminada exitosamente',
        () => 'Error al eliminar la tarifa',
      );
      if (ok !== null) {
        this.pricingTiers.update(list => list.filter(t => t.tier_id !== tier.tier_id));
      }
    } catch (error) {
      console.error('Error al eliminar tarifa:', error);
    } finally {
      this.deletingTier.set(null);
    }
  }

  get editFullAddress(): string {
    const value = this.editForm?.value || {};
    return [value.address_line1, value.city, value.department, value.country].filter(Boolean).join(', ');
  }

  setEditMapLocation(location: SpaceMapLocation): void {
    this.editForm.patchValue({ latitude: location.latitude, longitude: location.longitude, google_place_id: location.google_place_id || null });
    this.editForm.markAsDirty();
  }

  linkCopied = signal(false);

  publicSpaceUrl(): string {
    const id = this.espacio()?.space_id;
    return id ? `${environment.clientAppUrl}/espacio/${id}` : '';
  }

  async copyPublicSpaceUrl(): Promise<void> {
    const url = this.publicSpaceUrl();
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      this.linkCopied.set(true);
      setTimeout(() => this.linkCopied.set(false), 2000);
    } catch {
      this.notifications.error('No se pudo copiar el enlace. Cópialo manualmente.');
    }
  }

  reloadEspacio(): void {
    const espacio = this.espacio();
    if (espacio) {
      // Recargar el espacio completo desde el servicio
      const espacioActualizado = this.salaService.espacios().find(e => e.space_id === espacio.space_id);
      if (espacioActualizado) {
        this.espacio.set({ ...espacioActualizado });
      }
    }
  }

  async saveEditInfo(): Promise<void> {
    if (!this.cooldown.consume('sala-save-details')) return;
    const invalidField = firstInvalidControlName(this.editForm);
    if (invalidField) this.activeEditTab.set(this.EDIT_TAB_BY_FIELD[invalidField] ?? 'general');
    if (validateAndFocusFirstError(this.editForm, this.editFormRef?.nativeElement, msg => this.notifications.error(msg), 'Completa correctamente los campos marcados antes de guardar.')) {
      const formValue = this.editForm.value;
      const espacio = this.espacio();

      if (espacio) {
        const updateRequest: Partial<CreateEspacioRequest> = {
          title: formValue.title,
          description: formValue.description,
          category: formValue.categories[0],
          categories: formValue.categories,
          capacity: formValue.capacity,
          price_per_hour: formValue.price_per_hour,
          address_line1: formValue.address_line1,
          city: formValue.city,
          department: formValue.department,
          country: formValue.country,
          ...(formValue.latitude !== null && formValue.longitude !== null ? {
            latitude: Number(formValue.latitude), longitude: Number(formValue.longitude), google_place_id: formValue.google_place_id || null,
          } : {}),
          is_active: formValue.is_active
        };
        const normalizeCategories = (values: string[] | undefined) => [...(values || [])].map(value => String(value).trim()).sort();
        const currentComparable = {
          title: String(espacio.title || '').trim(), description: String(espacio.description || '').trim(),
          categories: normalizeCategories(espacio.categories?.length ? espacio.categories : [espacio.category || 'Otro']),
          capacity: Number(espacio.capacity), price_per_hour: Number(espacio.price_per_hour), address_line1: String(espacio.address_line1 || '').trim(),
          city: String(espacio.city || '').trim(), department: String(espacio.department || '').trim(), country: String(espacio.country || '').trim(),
          latitude: espacio.latitude == null ? null : Number(espacio.latitude), longitude: espacio.longitude == null ? null : Number(espacio.longitude), is_active: !!espacio.is_active,
        };
        const updatedComparable = {
          title: String(updateRequest.title || '').trim(), description: String(updateRequest.description || '').trim(), categories: normalizeCategories(updateRequest.categories),
          capacity: Number(updateRequest.capacity), price_per_hour: Number(updateRequest.price_per_hour), address_line1: String(updateRequest.address_line1 || '').trim(),
          city: String(updateRequest.city || '').trim(), department: String(updateRequest.department || '').trim(), country: String(updateRequest.country || '').trim(),
          latitude: updateRequest.latitude == null ? null : Number(updateRequest.latitude), longitude: updateRequest.longitude == null ? null : Number(updateRequest.longitude), is_active: !!updateRequest.is_active,
        };
        if (JSON.stringify(currentComparable) === JSON.stringify(updatedComparable)) {
          this.notifications.error('No hay cambios por guardar en la información del espacio.');
          return;
        }

        try {
          const espacioActualizado = await this.dialogs.run(
            'Guardando cambios…',
            () => this.salaService.actualizarEspacio(espacio.space_id, updateRequest),
            'Espacio actualizado exitosamente',
            () => 'Error al actualizar el espacio',
          );
          if (espacioActualizado !== null) {
            const espacioConExtras = {
              ...espacioActualizado,
              images: espacio.images,
              extras: espacio.extras
            };

            this.espacio.set(espacioConExtras);

            const espaciosActuales = this.salaService.espacios();
            const espaciosActualizados = espaciosActuales.map(e =>
              e.space_id === espacio.space_id
                ? { ...espacioConExtras }
                : e
            );
            this.salaService.espacios.set(espaciosActualizados);

            this.closeEditModal();
          }
        } catch (error) {
          console.error('Error al actualizar espacio:', error);
        }
      }
    }
  }

  togglePublishStatus(): void {
    const espacio = this.espacio();
    if (!espacio) return;

    const nuevoEstado = !espacio.is_active;
    this.salaService.toggleEspacioStatus(espacio.space_id, nuevoEstado).subscribe({
      next: (espacioActualizado) => {
        const espacioConExtras = {
          ...espacioActualizado,
          images: espacio.images,
          extras: espacio.extras
        };
        this.espacio.set(espacioConExtras);

        const espaciosActuales = this.salaService.espacios();
        const espaciosActualizados = espaciosActuales.map(e =>
          e.space_id === espacio.space_id
            ? { ...espacioConExtras }
            : e
        );
        this.salaService.espacios.set(espaciosActualizados);

        this.notifications.success(
          nuevoEstado ? 'Espacio publicado exitosamente' : 'Espacio despublicado'
        );
      },
      error: (error) => {
        console.error('Error al cambiar estado del espacio:', error);
        this.notifications.error('Error al cambiar el estado del espacio');
      }
    });
  }

  /** El editor de extras guardó cambios: la vista local queda al día. */
  onExtrasChange(extras: EspacioExtra[]): void {
    const espacio = this.espacio();
    if (espacio) this.espacio.set({ ...espacio, extras: extras as any });
  }

  imagenActual(): string | null {
    if (!this.espacio() || !this.espacio()!.images || this.espacio()!.images.length === 0) {
      return null;
    }
    return this.espacio()!.images[this.currentImageIndex()].image_url;
  }

  getImageUrl(imageUrl: string): string {
    return `${this.IMAGES_BASE_URL}${imageUrl}`;
  }

  handleImageError(event: any): void {
    // Si hay un error cargando la imagen, ocultar el elemento img
    event.target.style.display = 'none';
  }

  selectImage(index: number): void {
    this.currentImageIndex.set(index);
  }

  async setFeaturedImage(image: EspacioImage): Promise<void> {
    if (!image.image_id || image.is_featured || this.isSavingFeatured()) return;
    const imageId = image.image_id;
    this.isSavingFeatured.set(true);
    try {
      const ok = await this.dialogs.run(
        'Actualizando portada…',
        () => this.salaService.destacarImagen(imageId),
        'Imagen de portada actualizada',
        () => 'No fue posible actualizar la imagen de portada',
      );
      if (ok !== null) {
        const space = this.espacio();
        if (space) {
          const images = space.images
            .map(item => ({ ...item, is_featured: item.image_id === image.image_id }))
            .sort((a, b) => Number(!!b.is_featured) - Number(!!a.is_featured) || Number(a.image_id) - Number(b.image_id));
          const updated = { ...space, images };
          this.espacio.set(updated);
          this.currentImageIndex.set(0);
          this.salaService.espacios.update(spaces => spaces.map(item => item.space_id === updated.space_id ? updated : item));
        }
      }
    } catch {
      // El error ya se muestra en el modal de resultado (confirmAndRun).
    } finally {
      this.isSavingFeatured.set(false);
    }
  }

  goBack(): void {
    this.navigateToSpaces();
  }

  private navigateToSpaces(): void {
    void this.router.navigate(['/dashboard'], {
      queryParams: { module: 'salas' }
    });
  }

  isDeletingImage(image?: EspacioImage): boolean {
    if (!image) {
      // Para el botón principal
      return this.deletingImage() !== null;
    }
    // Para la galería
    return this.deletingImage() === image.image_id;
  }

  async deleteImage(): Promise<void> {
    if (!this.cooldown.consume('sala-delete-image')) return;
    const image = this.currentImage();
    if (!image || !image.image_id) return;
    const imageId = image.image_id;

    this.deletingImage.set(imageId);
    try {
      const ok = await this.dialogs.confirmAndRun(
        { title: 'Eliminar imagen', message: 'La imagen dejará de mostrarse en esta sala.', tone: 'danger', confirmText: 'Eliminar' },
        () => this.salaService.eliminarImagen(imageId),
        'Imagen eliminada exitosamente',
        () => 'Error al eliminar la imagen',
      );
      if (ok !== null) {
        // Actualizar localmente
        const espacio = this.espacio();
        if (espacio) {
          let nuevasImagenes = espacio.images.filter(img => img.image_id !== imageId);
          if (image.is_featured && nuevasImagenes.length) {
            nuevasImagenes = nuevasImagenes.map((img, index) => ({ ...img, is_featured: index === 0 }));
          }
          const updatedSpace = {
            ...espacio,
            images: nuevasImagenes
          };
          this.espacio.set(updatedSpace);
          this.salaService.espacios.update(spaces => spaces.map(item => item.space_id === updatedSpace.space_id ? updatedSpace : item));
          // Ajustar el índice si es necesario
          if (this.currentImageIndex() >= nuevasImagenes.length && nuevasImagenes.length > 0) {
            this.currentImageIndex.set(nuevasImagenes.length - 1);
          }
        }
      }
    } catch (error) {
      console.error('Error al eliminar imagen:', error);
    } finally {
      this.deletingImage.set(null);
    }
  }

  async deleteImageFromGallery(image: EspacioImage): Promise<void> {
    if (!image.image_id) return;
    const imageId = image.image_id;

    this.deletingImage.set(imageId);
    try {
      const ok = await this.dialogs.confirmAndRun(
        { title: 'Eliminar imagen', message: 'La imagen seleccionada se eliminará de la galería.', tone: 'danger', confirmText: 'Eliminar' },
        () => this.salaService.eliminarImagen(imageId),
        'Imagen eliminada exitosamente',
        () => 'Error al eliminar la imagen',
      );
      if (ok !== null) {
        // Actualizar localmente
        const espacio = this.espacio();
        if (espacio) {
          let nuevasImagenes = espacio.images.filter(img => img.image_id !== imageId);
          if (image.is_featured && nuevasImagenes.length) {
            nuevasImagenes = nuevasImagenes.map((img, index) => ({ ...img, is_featured: index === 0 }));
          }
          const updatedSpace = {
            ...espacio,
            images: nuevasImagenes
          };
          this.espacio.set(updatedSpace);
          this.salaService.espacios.update(spaces => spaces.map(item => item.space_id === updatedSpace.space_id ? updatedSpace : item));
          // Ajustar el índice si la imagen eliminada era la seleccionada
          if (this.currentImageIndex() >= nuevasImagenes.length && nuevasImagenes.length > 0) {
            this.currentImageIndex.set(nuevasImagenes.length - 1);
          } else if (nuevasImagenes.length === 0) {
            this.currentImageIndex.set(0);
          }
        }
      }
    } catch (error) {
      console.error('Error al eliminar imagen:', error);
    } finally {
      this.deletingImage.set(null);
    }
  }

  addImage(): void {
    this.triggerFileInput();
  }

  triggerFileInput(): void {
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.click();
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const files = Array.from(input.files);
      const espacio = this.espacio();
      
      if (espacio) {
        // El límite real depende del plan y se valida de forma segura en el backend.
        const remainingSlots = Number.MAX_SAFE_INTEGER;
        if (files.length > remainingSlots) {
          this.notifications.error(`Solo puedes agregar ${remainingSlots} imagen(es) más a este espacio.`);
          input.value = '';
          return;
        }
        const validationError = validateSpaceImages(files);
        if (validationError) {
          this.notifications.error(validationError);
          input.value = '';
          return;
        }
        this.uploadingImages.set(true);
        
        this.salaService.subirImagenes(espacio.space_id, files).subscribe({
          next: (response) => {
            this.uploadingImages.set(false);
            this.notifications.success('Imágenes subidas exitosamente');
            
            // Recargar el espacio para obtener las nuevas imágenes
            this.salaService.getEspacioById(espacio.space_id).subscribe({
              next: (espacioActualizado) => {
                this.espacio.set(espacioActualizado);
                
                // Actualizar también en el array global
                const espaciosActuales = this.salaService.espacios();
                const espaciosActualizados = espaciosActuales.map(e => 
                  e.space_id === espacio.space_id ? espacioActualizado : e
                );
                this.salaService.espacios.set(espaciosActualizados);
              }
            });
            
            // Limpiar el input
            if (this.fileInput?.nativeElement) {
              this.fileInput.nativeElement.value = '';
            }
          },
          error: (error) => {
            this.uploadingImages.set(false);
            console.error('Error al subir imágenes:', error);
            this.notifications.error('Error al subir las imágenes');
          }
        });
      }
    }
  }

  editInfo(): void {
    this.openEditModal();
  }

  deleteEspacio(): void {
    const espacio = this.espacio();
    if (!espacio) return;
    this.dialogs.confirmAndRun(
      {
        title: 'Eliminar espacio definitivamente',
        message: 'Se eliminarán el espacio, imágenes, reservas, pagos relacionados, extras, horarios y estadísticas. Esta acción no se puede deshacer.',
        confirmText: 'Eliminar definitivamente',
        danger: true,
      },
      () => this.salaService.eliminarEspacio(espacio.space_id),
      'Espacio eliminado exitosamente.',
      error => friendlyError(error, 'Error al eliminar el espacio'),
    ).then(result => {
      if (result === null) return;
      this.salaService.loadEspacios();
      setTimeout(() => this.navigateToSpaces(), 1000);
    }).catch(() => {});
  }

  // Métodos para Horarios
  onDayClosedChange(index: number): void {
    const closedControl = this.scheduleForm.get(`day_${index}_closed`);
    if (closedControl?.value) {
      this.scheduleForm.get(`day_${index}_opening`)?.disable();
      this.scheduleForm.get(`day_${index}_closing`)?.disable();
    } else {
      this.scheduleForm.get(`day_${index}_opening`)?.enable();
      this.scheduleForm.get(`day_${index}_closing`)?.enable();
    }
  }

  openScheduleModal(): void {
    // Cargar horarios existentes en el formulario
    const schedules = this.spaceScheduleService.schedules();
    const formControls: any = {};
    
    this.daysOfWeek.forEach((day, index) => {
      const existingSchedule = schedules.find(s => s.day_of_week === day.value);
      if (existingSchedule) {
        formControls[`day_${index}_opening`] = [this.spaceScheduleService.formatTime(existingSchedule.opening_time)];
        formControls[`day_${index}_closing`] = [this.spaceScheduleService.formatTime(existingSchedule.closing_time)];
        formControls[`day_${index}_closed`] = [existingSchedule.is_closed];
      } else {
        formControls[`day_${index}_opening`] = ['08:00'];
        formControls[`day_${index}_closing`] = ['17:00'];
        formControls[`day_${index}_closed`] = [false];
      }
    });
    
    this.scheduleForm = this.fb.group(formControls);
    this.scheduleSubmitted.set(false);
    this.scheduleGen++;
    this.showScheduleModal.set(true);
  }

  closeScheduleModal(): void {
    this.showScheduleModal.set(false);
  }

  trackScheduleDay = (index: number) => `${this.scheduleGen}-${index}`;

  isScheduleDayClosed(index: number): boolean {
    return !!this.scheduleForm?.get(`day_${index}_closed`)?.value;
  }

  setScheduleDayOpen(index: number, open: boolean): void {
    this.scheduleForm.get(`day_${index}_closed`)?.setValue(!open);
    this.onDayClosedChange(index);
  }

  applySchedulePreset(preset: 'weekdays' | 'all'): void {
    this.scheduleGen++;
    this.daysOfWeek.forEach((day, index) => {
      const closed = preset === 'weekdays' && (day.value === 0 || day.value === 6);
      this.scheduleForm.patchValue({ [`day_${index}_opening`]: '08:00', [`day_${index}_closing`]: '17:00', [`day_${index}_closed`]: closed });
      this.onDayClosedChange(index);
    });
  }

  copyFirstOpenScheduleDay(): void {
    const first = this.daysOfWeek.findIndex((_, index) => !this.isScheduleDayClosed(index));
    if (first < 0) { this.notifications.error('Abre al menos un día para copiar su horario.'); return; }
    const opening = this.scheduleForm.get(`day_${first}_opening`)?.value;
    const closing = this.scheduleForm.get(`day_${first}_closing`)?.value;
    this.scheduleGen++;
    this.daysOfWeek.forEach((_, index) => {
      if (!this.isScheduleDayClosed(index)) this.scheduleForm.patchValue({ [`day_${index}_opening`]: opening, [`day_${index}_closing`]: closing });
    });
  }

  /** Error de un día abierto: horas faltantes, minutos distintos de :00/:30 o cierre no posterior. */
  scheduleDayError(index: number): string | null {
    if (this.isScheduleDayClosed(index)) return null;
    const opening = String(this.scheduleForm.get(`day_${index}_opening`)?.value || '').slice(0, 5);
    const closing = String(this.scheduleForm.get(`day_${index}_closing`)?.value || '').slice(0, 5);
    if (!/^\d{2}:\d{2}$/.test(opening) || !/^\d{2}:\d{2}$/.test(closing)) return 'Indica la hora de apertura y de cierre.';
    if (![opening, closing].every(time => ['00', '30'].includes(time.slice(3)))) return 'Usa horas en punto o y media.';
    if (closing <= opening) return 'El cierre debe ser después de la apertura.';
    return null;
  }

  allScheduleDaysClosed(): boolean {
    return this.daysOfWeek.every((_, index) => this.isScheduleDayClosed(index));
  }

  async saveSchedules(): Promise<void> {
    this.scheduleSubmitted.set(true);
    const invalidDay = this.daysOfWeek.findIndex((_, index) => !!this.scheduleDayError(index));
    if (invalidDay >= 0 || this.allScheduleDaysClosed()) {
      setTimeout(() => document.querySelector('.sched-day.invalid, .sched-err-block')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 30);
      return;
    }
    if (!this.cooldown.consume('sala-save-schedules')) return;
    const espacio = this.espacio();
    if (!espacio) return;

    this.isSavingSchedules.set(true);
    const schedules: CreateScheduleRequest[] = [];

    this.daysOfWeek.forEach((day, index) => {
      const closed = this.scheduleForm.get(`day_${index}_closed`)?.value;
      schedules.push({
        day_of_week: day.value,
        opening_time: closed ? '00:00' : this.scheduleForm.get(`day_${index}_opening`)?.value || '08:00',
        closing_time: closed ? '00:00' : this.scheduleForm.get(`day_${index}_closing`)?.value || '17:00',
        is_closed: closed
      });
    });

    const request: BatchScheduleRequest = { schedules };

    try {
      const response = await this.dialogs.run(
        'Guardando horarios…',
        () => this.spaceScheduleService.setSchedules(espacio.space_id, request),
        'Horarios guardados exitosamente',
        (error: any) => friendlyError(error, 'Error al guardar los horarios'),
      );
      if (response !== null) {
        this.spaceScheduleService.schedules.set(response.schedules);
        this.closeScheduleModal();
      }
    } catch (error) {
      console.error('Error al guardar horarios:', error);
    } finally {
      this.isSavingSchedules.set(false);
    }
  }

  async setStandardWeekday(): Promise<void> {
    const espacio = this.espacio();
    if (!espacio) return;

    this.isSavingSchedules.set(true);
    const request: StandardWeekdayRequest = {
      opening_time: '08:00',
      closing_time: '17:00'
    };

    try {
      const response = await this.dialogs.confirmAndRun(
        { title: 'Aplicar horario estándar', message: 'Se configurará de lunes a viernes, de 8:00 AM a 5:00 PM.', tone: 'warning', confirmText: 'Aplicar horario' },
        () => this.spaceScheduleService.setStandardWeekday(espacio.space_id, request),
        'Horario estándar configurado exitosamente',
        (error: any) => friendlyError(error, 'Error al configurar el horario'),
      );
      if (response !== null) {
        this.spaceScheduleService.schedules.set(response.schedules);
      }
    } catch (error) {
      console.error('Error al configurar horario estándar:', error);
    } finally {
      this.isSavingSchedules.set(false);
    }
  }

  editSchedule(schedule: SpaceSchedule): void {
    this.editingSchedule.set(schedule);
    this.initEditScheduleForm(schedule);
  }

  closeEditScheduleModal(): void {
    this.editingSchedule.set(null);
  }

  async saveEditSchedule(): Promise<void> {
    const schedule = this.editingSchedule();
    if (!schedule) return;
    if (!validateAndFocusFirstError(this.editScheduleForm, this.editScheduleFormRef?.nativeElement, msg => this.notifications.error(msg), 'Completa correctamente los horarios antes de guardar.')) return;

    this.isSavingSchedule.set(true);
    const updateRequest = {
      opening_time: this.editScheduleForm.get('is_closed')?.value ? '00:00' : this.editScheduleForm.get('opening_time')?.value,
      closing_time: this.editScheduleForm.get('is_closed')?.value ? '00:00' : this.editScheduleForm.get('closing_time')?.value,
      is_closed: this.editScheduleForm.get('is_closed')?.value
    };

    try {
      const updatedSchedule = await this.dialogs.run(
        'Guardando horario…',
        () => this.spaceScheduleService.updateSchedule(schedule.schedule_id, updateRequest),
        'Horario actualizado exitosamente',
        (error: any) => friendlyError(error, 'Error al actualizar el horario'),
      );
      if (updatedSchedule !== null) {
        const schedules = this.spaceScheduleService.schedules();
        const index = schedules.findIndex(s => s.schedule_id === schedule.schedule_id);
        if (index !== -1) {
          schedules[index] = updatedSchedule;
          this.spaceScheduleService.schedules.set([...schedules]);
        }
        this.closeEditScheduleModal();
      }
    } catch (error) {
      console.error('Error al actualizar horario:', error);
    } finally {
      this.isSavingSchedule.set(false);
    }
  }

  requestDeleteSchedule(schedule: SpaceSchedule): void {
    this.schedulePendingDeletion.set(schedule);
  }

  closeDeleteScheduleModal(): void {
    if (!this.deletingSchedule()) this.schedulePendingDeletion.set(null);
  }

  confirmDeleteSchedule(): void {
    const schedule = this.schedulePendingDeletion();
    if (!schedule || this.deletingSchedule()) return;
    this.deletingSchedule.set(true);
    this.spaceScheduleService.deleteSchedule(schedule.schedule_id).subscribe({
      next: () => {
        const schedules = this.spaceScheduleService.schedules().filter(s => s.schedule_id !== schedule.schedule_id);
        this.spaceScheduleService.schedules.set(schedules);
        this.deletingSchedule.set(false);
        this.schedulePendingDeletion.set(null);
        this.notifications.success('Horario eliminado exitosamente');
      },
      error: (error) => {
        this.deletingSchedule.set(false);
        console.error('Error al eliminar horario:', error);
        const errorMessage = friendlyError(error, 'Error al eliminar el horario');
        this.notifications.error(errorMessage);
      }
    });
  }
}
